<template>
    <div>
        <!-- 단위요금 설정 모달 (최상위 레벨) -->
        <div v-if="show && selectedSlot" class="price-setting-modal-overlay" @click="closeBlockEditor">
            <div class="price-setting-modal" @click.stop>
                <div class="price-setting-modal-header">
                    <h4>단위요금 설정</h4>
                    <button class="btn-close-modal" @click="closeBlockEditor" title="닫기">✕</button>
                </div>
                <div class="price-setting-modal-body">
                    <div
                        v-if="selectedSlot.hourStart !== undefined && selectedSlot.hourEnd !== undefined"
                        class="form-group-inline"
                    >
                        <div class="form-field">
                            <label>시작 시간:</label>
                            <input
                                type="number"
                                v-model.number="selectedSlot.hourStart"
                                @input="onSlotPropertyChange"
                                min="0"
                                max="23"
                                class="form-input"
                            />
                        </div>
                        <div class="form-field">
                            <label>종료 시간:</label>
                            <input
                                type="number"
                                v-model.number="selectedSlot.hourEnd"
                                @input="onSlotPropertyChange"
                                min="0"
                                max="24"
                                class="form-input"
                            />
                            <small style="display: block; margin-top: 4px; color: var(--ebc-text-sub)">
                                * 종료 시간은 미포함입니다. (예: 10 입력 시 0시~9시까지 포함)
                            </small>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>부하 구분:</label>
                        <select v-model="selectedSlot.timeSlotCode" @change="onSlotPropertyChange" class="form-select">
                            <option value="LOW">경부하</option>
                            <option value="MID">중부하</option>
                            <option value="PEAK">최대부하</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>{{ unitPriceLabel }}:</label>
                        <input
                            type="number"
                            v-model.number="selectedSlot.unitPrice"
                            @input="onUnitPriceChange"
                            step="0.01"
                            class="form-input"
                        />
                        <small style="display: block; margin-top: 4px; color: var(--ebc-text-sub)">
                            * 동일한 부하 구분의 다른 구간에도 자동으로 적용됩니다.
                        </small>
                    </div>
                    <div class="button-group">
                        <button @click="openMergeDialog" class="btn-merge">병합</button>
                        <button @click="deleteTimeSlotBlock" class="btn-delete">삭제</button>
                    </div>
                </div>
            </div>
        </div>

        <SettingsSidePanel
            :show="show"
            :title="`단위요금 설정: ${costInfo.costName || ''}`"
            size="wide"
            @close="closePanel"
        >
            <!-- 요금제 기본 정보 + 조합 방식 통합 -->
            <div class="cost-info-compact">
                <div class="info-items">
                    <div class="info-item">
                        <span class="info-label">공급자</span>
                        <span class="info-value">{{ costInfo.supplierName }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">요금제 타입</span>
                        <span class="info-value">{{ getTypeName(costInfo.baseFeeCalcType) }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">기본요금</span>
                        <span class="info-value">{{ formatNumber(costInfo.baseFeeValue) }}원</span>
                    </div>
                    <!-- <div class="info-divider"></div> -->
                    <div class="info-item structure-item" :class="structureType">
                        <span class="structure-badge">{{ structureTitle }}</span>
                        <span class="structure-desc">{{ structureDescription }}</span>
                    </div>
                </div>
            </div>

            <!-- 단위요금 섹션 -->
            <div class="detail-section">
                <div class="detail-header">
                    <h5>단위요금 정보</h5>
                    <div class="detail-actions">
                        <!-- 복제 기능 (누진제, 단일요금제 제외) -->
                        <div
                            v-if="!isProgressiveType && !isFlatCostType && dimensionCount > 0"
                            class="copy-cost-section"
                        >
                            <select v-model="selectedCopyCostIdx" class="copy-cost-select">
                                <option :value="null">동일 구조 요금제 선택</option>
                                <option v-for="cost in compatibleCosts" :key="cost.costIdx" :value="cost.costIdx">
                                    {{ cost.costName }}
                                </option>
                            </select>
                            <button
                                class="btn-action btn-sm btn-secondary"
                                @click="copyCostDetails"
                                :disabled="!selectedCopyCostIdx"
                            >
                                <i class="fas fa-copy"></i>
                                복제
                            </button>
                        </div>
                        <button
                            v-if="isProgressiveType"
                            class="btn-action btn-sm btn-primary"
                            @click="addProgressiveDetail"
                        >
                            구간 추가
                        </button>
                        <!-- <button
                        v-if="!isProgressiveType"
                        class="btn-action btn-sm btn-excel"
                        @click="importFromExcel"
                    >
                        Excel 업로드
                    </button> -->
                    </div>
                </div>
            </div>

            <!-- 타입 A: 누진제 (사용량 구간) -->
            <div v-if="isProgressiveType" class="progressive-table">
                <table class="detail-table">
                    <thead>
                        <tr>
                            <th>구간</th>
                            <th>사용량 범위 (kWh) <span class="required">*</span></th>
                            <th>{{ unitPriceLabel }} <span class="required">*</span></th>
                            <th>작업</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(detail, idx) in progressiveDetails" :key="idx">
                            <td class="text-center">{{ idx + 1 }}</td>
                            <td>
                                <div class="range-inputs">
                                    <input
                                        type="number"
                                        v-model.number="detail.usageFrom"
                                        placeholder="시작"
                                        class="input-sm"
                                        required
                                    />
                                    <span>~</span>
                                    <input
                                        type="number"
                                        v-model.number="detail.usageTo"
                                        placeholder="무제한"
                                        class="input-sm"
                                    />
                                </div>
                            </td>
                            <td>
                                <input
                                    type="number"
                                    v-model.number="detail.unitPrice"
                                    placeholder="단위요금"
                                    class="input-sm"
                                    step="0.01"
                                />
                            </td>
                            <td class="text-center">
                                <button
                                    class="btn-delete"
                                    @click="deleteProgressiveDetail(idx)"
                                    :disabled="progressiveDetails.length <= 1"
                                >
                                    <i class="fas fa-trash-alt"></i>
                                    삭제
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- 타입 B: 차원별 (1D, 2D, 3D) -->
            <div v-else-if="dimensionCount > 0" class="dimensional-section">
                <!-- 3차원: 계절 × 시간대 × 요일 (Excel 스타일 블록 리스트) -->
                <div v-if="dimensionCount === 3" class="triple-dimension">
                    <!-- 계절별 탭 -->
                    <div class="season-tabs">
                        <button
                            v-for="season in seasonOptions"
                            :key="season.code"
                            :class="['season-tab', { active: selectedSeason === season.code }]"
                            @click="selectedSeason = season.code"
                        >
                            {{ season.label }}
                        </button>
                    </div>

                    <!-- 주중/주말/공휴일 블록 리스트 -->
                    <div class="day-type-blocks-container">
                        <div class="time-slot-help-text">
                            ※ 종료 시간은 해당 시간을 포함하지 않습니다. (예: 종료 시간이 10이면 0시~9시 59분까지
                            적용됩니다)
                        </div>
                        <!-- 주중 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>{{ getSeasonLabelForDisplay(selectedSeason) }} - 주중 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock3D(selectedSeason, 'WEEKDAY')">
                                    + 추가
                                </button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-weekday-${getTimeSlots(selectedSeason, 'WEEKDAY').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots(selectedSeason, 'WEEKDAY')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange3D(selectedSeason, 'WEEKDAY', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions3D(
                                                            selectedSeason,
                                                            'WEEKDAY',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-weekday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions3D(
                                                            selectedSeason,
                                                            'WEEKDAY',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange3D(selectedSeason, 'WEEKDAY', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock3D(selectedSeason, 'WEEKDAY', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock3D(selectedSeason, 'WEEKDAY', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 주말 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>{{ getSeasonLabelForDisplay(selectedSeason) }} - 주말 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock3D(selectedSeason, 'WEEKEND')">
                                    + 추가
                                </button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-weekend-${getTimeSlots(selectedSeason, 'WEEKEND').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots(selectedSeason, 'WEEKEND')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange3D(selectedSeason, 'WEEKEND', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions3D(
                                                            selectedSeason,
                                                            'WEEKEND',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-holiday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions3D(
                                                            selectedSeason,
                                                            'WEEKEND',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange3D(selectedSeason, 'WEEKEND', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock3D(selectedSeason, 'WEEKEND', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock3D(selectedSeason, 'WEEKEND', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 공휴일 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>{{ getSeasonLabelForDisplay(selectedSeason) }} - 공휴일 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock3D(selectedSeason, 'HOLIDAY')">
                                    + 추가
                                </button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-holiday-${getTimeSlots(selectedSeason, 'HOLIDAY').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots(selectedSeason, 'HOLIDAY')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange3D(selectedSeason, 'HOLIDAY', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions3D(
                                                            selectedSeason,
                                                            'HOLIDAY',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-holiday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions3D(
                                                            selectedSeason,
                                                            'HOLIDAY',
                                                            block,
                                                        )"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange3D(selectedSeason, 'HOLIDAY', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock3D(selectedSeason, 'HOLIDAY', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock3D(selectedSeason, 'HOLIDAY', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- 2차원: 계절 × 시간대 (Excel 스타일 블록 리스트) -->
                <div
                    v-else-if="dimensionCount === 2 && costInfo.hasSeason === 1 && costInfo.hasTimeSlot === 1"
                    class="dual-dimension-season"
                >
                    <!-- 계절별 탭 -->
                    <div class="season-tabs">
                        <button
                            v-for="season in seasonOptions"
                            :key="season.code"
                            :class="['season-tab', { active: selectedSeason === season.code }]"
                            @click="selectedSeason = season.code"
                        >
                            {{ season.label }}
                        </button>
                    </div>

                    <!-- Excel 스타일 블록 리스트 -->
                    <div class="time-slot-blocks-container">
                        <div class="blocks-header">
                            <h4>{{ getSeasonLabelForDisplay(selectedSeason) }} 단위요금</h4>
                            <button class="btn-add-block" @click="addTimeSlotBlock2D(selectedSeason)">
                                + 시간대 추가
                            </button>
                        </div>
                        <div class="time-slot-help-text">
                            ※ 종료 시간은 해당 시간을 포함하지 않습니다. (예: 종료 시간이 10이면 0시~9시 59분까지
                            적용됩니다)
                        </div>
                        <table
                            class="time-slot-blocks-table"
                            :key="`table-2d-${getTimeSlots2D(selectedSeason).length}`"
                        >
                            <thead>
                                <tr>
                                    <th style="width: 30%; text-align: center">시간대</th>
                                    <th style="width: 18%; text-align: center">부하 구분</th>
                                    <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                    <th style="width: auto; text-align: center">작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(block, idx) in getTimeSlots2D(selectedSeason)"
                                    :key="block.id"
                                    class="block-row"
                                    :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                >
                                    <td>
                                        <div style="display: flex; gap: 8px; align-items: center">
                                            <select
                                                v-model="block.hourStart"
                                                @change="onStartHourChange2D(selectedSeason, block, idx)"
                                                class="time-select"
                                                style="flex: 1"
                                            >
                                                <option
                                                    v-for="option in getStartHourOptions2D(selectedSeason, block)"
                                                    :key="option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                            <span style="color: var(--ebc-text-sub)">~</span>
                                            <select
                                                v-model="block.hourEnd"
                                                :key="`end-2d-${block.id}-${block.hourStart || 'null'}`"
                                                class="time-select"
                                                style="flex: 1"
                                            >
                                                <option
                                                    v-for="option in getEndHourOptions2D(selectedSeason, block)"
                                                    :key="option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            v-model="block.timeSlotCode"
                                            @change="$forceUpdate()"
                                            class="load-type-select"
                                        >
                                            <option value="LOW">경부하</option>
                                            <option value="MID">중부하</option>
                                            <option value="PEAK">최대부하</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            v-model.number="block.unitPrice"
                                            @input="onUnitPriceChange2D(selectedSeason, block)"
                                            step="0.01"
                                            class="price-input"
                                            placeholder="단위요금"
                                        />
                                    </td>
                                    <td class="action-cell">
                                        <button
                                            class="btn-split"
                                            @click="splitTimeSlotBlock2D(selectedSeason, idx)"
                                            title="분할"
                                        >
                                            <i class="fas fa-cut"></i>
                                            분할
                                        </button>
                                        <button
                                            class="btn-delete"
                                            @click="deleteTimeSlotBlock2D(selectedSeason, idx)"
                                            title="삭제"
                                        >
                                            <i class="fas fa-trash-alt"></i>
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- 2차원: 요일 × 시간대 (Excel 스타일 블록 리스트) -->
                <div
                    v-else-if="dimensionCount === 2 && costInfo.hasDayType === 1 && costInfo.hasTimeSlot === 1"
                    class="dual-dimension-day"
                >
                    <div class="day-type-blocks-container">
                        <div class="time-slot-help-text">
                            ※ 종료 시간은 해당 시간을 포함하지 않습니다. (예: 종료 시간이 10이면 0시~9시 59분까지
                            적용됩니다)
                        </div>
                        <!-- 주중 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>주중 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock2DDay('WEEKDAY')">+ 추가</button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-2dday-weekday-${getTimeSlots2DDay('WEEKDAY').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots2DDay('WEEKDAY')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange2DDay('WEEKDAY', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions2DDay('WEEKDAY', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-2dday-weekday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions2DDay('WEEKDAY', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange2DDay('WEEKDAY', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock2DDay('WEEKDAY', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock2DDay('WEEKDAY', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 주말 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>주말 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock2DDay('WEEKEND')">+ 추가</button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-2dday-weekend-${getTimeSlots2DDay('WEEKEND').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots2DDay('WEEKEND')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange2DDay('WEEKEND', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions2DDay('WEEKEND', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-holiday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions2DDay('WEEKEND', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange2DDay('WEEKEND', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock2DDay('WEEKEND', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock2DDay('WEEKEND', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 공휴일 블록 리스트 -->
                        <div class="day-type-blocks">
                            <div class="blocks-header">
                                <h4>공휴일 단위요금</h4>
                                <button class="btn-add-block" @click="addTimeSlotBlock2DDay('HOLIDAY')">+ 추가</button>
                            </div>
                            <table
                                class="time-slot-blocks-table"
                                :key="`table-2dday-holiday-${getTimeSlots2DDay('HOLIDAY').length}`"
                            >
                                <thead>
                                    <tr>
                                        <th style="width: 30%; text-align: center">시간대</th>
                                        <th style="width: 18%; text-align: center">부하 구분</th>
                                        <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                        <th style="width: auto; text-align: center">작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(block, idx) in getTimeSlots2DDay('HOLIDAY')"
                                        :key="block.id"
                                        class="block-row"
                                        :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                    >
                                        <td>
                                            <div style="display: flex; gap: 8px; align-items: center">
                                                <select
                                                    v-model="block.hourStart"
                                                    @change="onStartHourChange2DDay('HOLIDAY', block, idx)"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getStartHourOptions2DDay('HOLIDAY', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                                <span style="color: var(--ebc-text-sub)">~</span>
                                                <select
                                                    v-model="block.hourEnd"
                                                    :key="`end-2dday-holiday-${block.id}-${block.hourStart || 'null'}`"
                                                    class="time-select"
                                                    style="flex: 1"
                                                >
                                                    <option
                                                        v-for="option in getEndHourOptions2DDay('HOLIDAY', block)"
                                                        :key="option.value"
                                                        :value="option.value"
                                                    >
                                                        {{ option.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                v-model="block.timeSlotCode"
                                                @change="$forceUpdate()"
                                                class="load-type-select"
                                            >
                                                <option value="LOW">경부하</option>
                                                <option value="MID">중부하</option>
                                                <option value="PEAK">최대부하</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                v-model.number="block.unitPrice"
                                                @input="onUnitPriceChange2DDay('HOLIDAY', block)"
                                                step="0.01"
                                                class="price-input"
                                                placeholder="단위요금"
                                            />
                                        </td>
                                        <td class="action-cell">
                                            <button
                                                class="btn-split"
                                                @click="splitTimeSlotBlock2DDay('HOLIDAY', idx)"
                                                title="분할"
                                            >
                                                <i class="fas fa-cut"></i>
                                                분할
                                            </button>
                                            <button
                                                class="btn-delete"
                                                @click="deleteTimeSlotBlock2DDay('HOLIDAY', idx)"
                                                title="삭제"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- 1차원: 계절만 (2차원과 동일한 테이블 비주얼만, 탭 없음) -->
                <div v-else-if="dimensionCount === 1 && costInfo.hasSeason === 1" class="dual-dimension-season">
                    <div class="time-slot-blocks-container">
                        <div class="blocks-header">
                            <h4>계절별 단위요금</h4>
                        </div>
                        <table class="time-slot-blocks-table">
                            <thead>
                                <tr>
                                    <th style="width: 30%; text-align: center">계절</th>
                                    <th style="width: 70%; text-align: center">{{ unitPriceLabel }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(detail, idx) in simpleDetails" :key="detail.id || idx" class="block-row">
                                    <td>{{ getSeasonLabel(detail.seasonCode) }}</td>
                                    <td>
                                        <input
                                            type="number"
                                            v-model.number="detail.unitPrice"
                                            @input="onSimpleDetailUnitPriceChange(detail)"
                                            placeholder="단위요금"
                                            class="price-input season-only-price-input"
                                            step="0.01"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- 1차원: 시간대만 (Excel 스타일 블록 리스트) -->
                <div v-else-if="dimensionCount === 1 && costInfo.hasTimeSlot === 1" class="single-dimension-time">
                    <div class="time-slot-blocks-container">
                        <div class="blocks-header">
                            <h4>단위요금</h4>
                            <button class="btn-add-block" @click="addTimeSlotBlock1D">+ 시간대 추가</button>
                        </div>
                        <div class="time-slot-help-text">
                            ※ 종료 시간은 해당 시간을 포함하지 않습니다. (예: 종료 시간이 10이면 0시~9시 59분까지
                            적용됩니다)
                        </div>
                        <table class="time-slot-blocks-table" :key="`table-1d-${getTimeSlots1D().length}`">
                            <thead>
                                <tr>
                                    <th style="width: 30%; text-align: center">시간대</th>
                                    <th style="width: 18%; text-align: center">부하 구분</th>
                                    <th style="width: 20%; text-align: center">{{ unitPriceLabel }}</th>
                                    <th style="width: auto; text-align: center">작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(block, idx) in getTimeSlots1D()"
                                    :key="block.id"
                                    class="block-row"
                                    :class="getLoadTypeClass1DByBlock(block.timeSlotCode)"
                                >
                                    <td>
                                        <div style="display: flex; gap: 8px; align-items: center">
                                            <select
                                                v-model="block.hourStart"
                                                @change="onStartHourChange1D(block, idx)"
                                                class="time-select"
                                                style="flex: 1"
                                            >
                                                <option
                                                    v-for="option in getStartHourOptions1D(block)"
                                                    :key="option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                            <span style="color: var(--ebc-text-sub)">~</span>
                                            <select
                                                v-model="block.hourEnd"
                                                :key="`end-1d-${block.id}-${block.hourStart || 'null'}`"
                                                class="time-select"
                                                style="flex: 1"
                                            >
                                                <option
                                                    v-for="option in getEndHourOptions1D(block)"
                                                    :key="option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            v-model="block.timeSlotCode"
                                            @change="$forceUpdate()"
                                            class="load-type-select"
                                        >
                                            <option value="LOW">경부하</option>
                                            <option value="MID">중부하</option>
                                            <option value="PEAK">최대부하</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            v-model.number="block.unitPrice"
                                            @input="onUnitPriceChange1D(block)"
                                            step="0.01"
                                            class="price-input"
                                            placeholder="단위요금"
                                        />
                                    </td>
                                    <td class="action-cell">
                                        <button class="btn-split" @click="splitTimeSlotBlock1D(idx)" title="분할">
                                            <i class="fas fa-cut"></i>
                                            분할
                                        </button>
                                        <button class="btn-delete" @click="deleteTimeSlotBlock1D(idx)" title="삭제">
                                            <i class="fas fa-trash-alt"></i>
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- 기존 방식 (시간대 개념 없음) -->
            <div v-else class="simple-dimension">
                <table class="detail-table">
                    <thead>
                        <tr>
                            <th v-if="shouldShowSeasonCode">계절</th>
                            <th v-if="shouldShowTimeSlotCode">시간대</th>
                            <th v-if="shouldShowDayType">요일</th>
                            <th>{{ unitPriceLabel }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(detail, idx) in simpleDetails" :key="idx">
                            <td v-if="shouldShowSeasonCode">
                                {{ getSeasonLabel(detail.seasonCode) }}
                            </td>
                            <td v-if="shouldShowTimeSlotCode">
                                {{ getTimeSlotLabel(detail.timeSlotCode) }}
                            </td>
                            <td v-if="shouldShowDayType">
                                {{ getDayTypeLabel(detail.dayType) }}
                            </td>
                            <td>
                                <input
                                    type="number"
                                    v-model.number="detail.unitPrice"
                                    @input="onSimpleDetailUnitPriceChange(detail)"
                                    placeholder="단위요금"
                                    class="input-sm"
                                    step="0.01"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <template #footer>
                <button class="btn-cancel" @click="closePanel">✕ 취소</button>
                <button class="btn-save" @click="handleSave">일괄 저장</button>
            </template>
        </SettingsSidePanel>
    </div>
</template>

<script>
import SettingsSidePanel from "../settings/SettingsSidePanel.vue";
import backEndApi from "@src/api/backEndApi.js";
import { shouldShowField } from "@/utils/fieldDisplayHelper";

export default {
    name: "CostDetailManagement",
    components: {
        SettingsSidePanel,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        costIdx: {
            type: Number,
            required: true,
        },
        costInfo: {
            type: Object,
            default: () => ({}),
        },
        costData: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            // Flat array를 그룹화한 구조
            timeSlotsByGroup: {},

            // 누진제용
            progressiveDetails: [],

            // 1D, 2D용
            simpleDetails: [],

            // 3차원용 선택된 계절
            selectedSeason: "MID",

            // 선택된 timeSlot
            selectedSlot: null,

            // 코드 매핑
            seasonOptions: [
                { code: "MID", label: "봄/가을" },
                { code: "SUMMER", label: "하계" },
                { code: "WINTER", label: "동계" },
            ],
            timeSlotOptions: [
                { code: "LOW", label: "경부하" },
                { code: "MID", label: "중부하" },
                { code: "PEAK", label: "최대부하" },
            ],
            dayTypeOptions: [
                { code: "WEEKDAY", label: "주중" },
                { code: "WEEKEND", label: "주말" },
                { code: "HOLIDAY", label: "공휴일" },
            ],

            autoMergeTimer: null,

            // 복제 기능 관련
            allCosts: [], // 전체 요금제 목록
            selectedCopyCostIdx: null, // 복제할 요금제 선택
        };
    },
    computed: {
        isProgressiveType() {
            return this.costInfo.hasProgressive === 1;
        },
        isFlatCostType() {
            return this.costInfo.hasFlatCost === 1;
        },
        dimensionCount() {
            if (this.isProgressiveType) return 0;
            return (this.costInfo.hasSeason || 0) + (this.costInfo.hasTimeSlot || 0) + (this.costInfo.hasDayType || 0);
        },
        // 동일한 구조를 가진 요금제 목록 (현재 요금제 제외, 누진제/단일요금제 제외)
        compatibleCosts() {
            return this.allCosts.filter((cost) => {
                // 현재 요금제 제외
                if (cost.costIdx === this.costIdx) return false;
                // 누진제 제외
                if (cost.hasProgressive === 1) return false;
                // 단일요금제 제외
                if (cost.hasFlatCost === 1) return false;
                // 동일한 구조인지 확인
                return (
                    cost.hasSeason === this.costInfo.hasSeason &&
                    cost.hasTimeSlot === this.costInfo.hasTimeSlot &&
                    cost.hasDayType === this.costInfo.hasDayType
                );
            });
        },
        structureType() {
            if (this.isProgressiveType) return "progressive";
            if (this.dimensionCount === 1) return "single";
            if (this.dimensionCount === 2) return "dual";
            if (this.dimensionCount === 3) return "triple";
            return "none";
        },
        structureTitle() {
            if (this.structureType === "progressive") {
                return "누진제 방식";
            }
            if (this.structureType === "triple") {
                return "계절 × 시간대 × 요일";
            }
            if (this.structureType === "none") {
                return "단위요금 구조 미설정";
            }

            // 1차원, 2차원: 선택된 차원에 따라 동적 생성
            const dimensions = [];
            if (this.shouldShowSeasonCode) dimensions.push("계절");
            if (this.shouldShowTimeSlotCode) dimensions.push("시간대");
            if (this.shouldShowDayType) dimensions.push("요일");

            if (dimensions.length === 1) {
                return `${dimensions[0]}별`;
            } else if (dimensions.length === 2) {
                return `${dimensions[0]} × ${dimensions[1]}`;
            }

            return "단위요금 구조 미설정";
        },
        // 필드 표시 여부 (유틸리티 함수 기반)
        shouldShowSeasonCode() {
            return shouldShowField("seasonCode", this.getContext());
        },
        shouldShowTimeSlotCode() {
            return shouldShowField("timeSlotCode", this.getContext());
        },
        shouldShowDayType() {
            return shouldShowField("dayType", this.getContext());
        },
        unitPriceLabel() {
            const currency = "원";
            const billingUnit = this.costInfo.billingUnit || "kWh";
            return `단위요금 (${currency}/${billingUnit})`;
        },
        structureDescription() {
            if (this.structureType === "progressive") {
                return "사용량 구간별로 단위요금을 설정합니다. 구간은 자유롭게 추가/삭제할 수 있습니다.";
            }
            if (this.structureType === "triple") {
                return "계절, 시간대, 요일의 모든 조합에 따라 단위요금이 달라집니다.";
            }
            if (this.structureType === "none") {
                return "단위요금 구조가 설정되지 않았습니다.";
            }

            // 1차원, 2차원: 선택된 차원에 따라 동적 생성
            const dimensions = [];
            if (this.shouldShowSeasonCode) dimensions.push("계절");
            if (this.shouldShowTimeSlotCode) dimensions.push("시간대");
            if (this.shouldShowDayType) dimensions.push("요일");

            if (dimensions.length === 1) {
                return `${dimensions[0]}별로 단위요금이 달라집니다.`;
            } else if (dimensions.length === 2) {
                return `${dimensions[0]}과 ${dimensions[1]} 조합에 따라 단위요금이 달라집니다.`;
            }

            return "";
        },
    },
    mounted() {
        // props로 데이터가 전달된 경우 사용, 없으면 API 호출
        if (this.costData && this.costData.details) {
            this.processDetails(this.costData.details);
        } else {
            this.loadDetails();
        }
        // 동일 구조 요금제 목록 로드
        this.loadAllCosts();
    },
    watch: {
        // show가 true로 변경될 때 데이터 로드
        show(newVal) {
            if (newVal) {
                if (this.costData && this.costData.details) {
                    this.processDetails(this.costData.details);
                } else {
                    this.loadDetails();
                }
                // 동일 구조 요금제 목록 다시 로드
                this.loadAllCosts();
            }
        },
        // costData가 변경될 때 데이터 처리
        costData: {
            handler(newVal) {
                if (newVal && newVal.details) {
                    this.processDetails(newVal.details);
                }
            },
            deep: true,
        },
    },
    methods: {
        /**
         * 필드 표시 규칙(shouldShowField)용 컨텍스트.
         * costInfo / costData 기준으로 부모와 동일한 형태 반환.
         */
        getContext() {
            const info = this.costInfo || {};
            const data = this.costData || {};
            return {
                baseFeeCalcType: info.baseFeeCalcType || data.baseFeeCalcType,
                hasFlatCost: info.hasFlatCost ?? data.hasFlatCost ?? 0,
                hasProgressive: info.hasProgressive ?? data.hasProgressive ?? 0,
                hasSeason: info.hasSeason ?? data.hasSeason ?? 0,
                hasTimeSlot: info.hasTimeSlot ?? data.hasTimeSlot ?? 0,
                hasDayType: info.hasDayType ?? data.hasDayType ?? 0,
                energySourceCode: data.supplier?.energySourceCode,
            };
        },
        // details 데이터 처리 (공통 로직)
        processDetails(apiDetails) {
            if (this.isProgressiveType) {
                // 누진제
                this.progressiveDetails = apiDetails.map((item, index) => ({
                    id: Date.now() + index,
                    usageFrom: parseInt(item.usageFrom || 0),
                    usageTo: item.usageTo ? parseInt(item.usageTo) : null,
                    unitPrice: parseFloat(item.unitPrice || 0),
                    baseFeeValue: parseFloat(item.baseFeeValue || 0),
                }));

                if (this.progressiveDetails.length === 0) {
                    this.progressiveDetails = [
                        { id: Date.now(), usageFrom: 0, usageTo: null, unitPrice: 0, baseFeeValue: 0 },
                    ];
                }
            } else if (this.costInfo.hasTimeSlot === 1) {
                // 시간대 개념이 있는 모든 경우 (3차원, 2차원, 1차원)
                this.timeSlotsByGroup = {};

                apiDetails.forEach((item, index) => {
                    // 그룹 키 생성
                    let key;
                    if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1) {
                        // 3차원: 계절 × 요일
                        key = `${item.seasonCode}_${item.dayType}`;
                    } else if (this.costInfo.hasSeason === 1) {
                        // 2차원: 계절만
                        key = item.seasonCode;
                    } else if (this.costInfo.hasDayType === 1) {
                        // 2차원: 요일만
                        key = item.dayType;
                    } else {
                        // 1차원: 시간대만
                        key = "DEFAULT";
                    }

                    if (!this.timeSlotsByGroup[key]) {
                        this.timeSlotsByGroup[key] = [];
                    }

                    this.timeSlotsByGroup[key].push({
                        id: Date.now() + index + Math.random(),
                        hourStart: parseInt(item.hourStart),
                        hourEnd: parseInt(item.hourEnd),
                        timeSlotCode: item.timeSlotCode,
                        unitPrice: parseFloat(item.unitPrice || 0),
                        seasonCode: item.seasonCode || null,
                        dayType: item.dayType || null,
                        selected: false,
                    });
                });

                // 각 그룹별로 시간순 정렬
                Object.keys(this.timeSlotsByGroup).forEach((key) => {
                    this.timeSlotsByGroup[key].sort((a, b) => a.hourStart - b.hourStart);
                });

                // 기본 데이터가 없으면 초기화
                if (Object.keys(this.timeSlotsByGroup).length === 0) {
                    this.initializeDefaultTimeSlots();
                }

                // 요일별인 경우: 주말/공휴일 데이터가 없으면 주중 기준으로 자동 반영
                if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1) {
                    this.fillWeekendFromWeekdayIfEmpty();
                }
                this.fillHolidayFromWeekdayIfEmpty();
            } else if (this.dimensionCount > 0) {
                // 시간대 개념이 없는 경우 (기존 방식)
                this.simpleDetails = apiDetails.map((item, index) => ({
                    id: Date.now() + index,
                    seasonCode: item.seasonCode,
                    timeSlotCode: item.timeSlotCode,
                    dayType: item.dayType,
                    unitPrice: parseFloat(item.unitPrice || 0),
                }));

                if (this.simpleDetails.length === 0) {
                    this.initializeSimpleDetails();
                }
            }
        },

        // API 호출로 데이터 로드 (fallback)
        async loadDetails() {
            try {
                const result = await backEndApi.energyBillCalc.getCost(this.costIdx);

                if (result.status === 200) {
                    const apiDetails = result.data.details || [];
                    this.processDetails(apiDetails);
                } else {
                    await this.alertWarning(result.data.message || "단위요금 정보를 불러오는데 실패했습니다.");
                }
            } catch (error) {
                console.error("단위요금 정보 조회 실패:", error);
                await this.alertWarning("단위요금 정보를 불러오는데 실패했습니다.");
            }
        },

        // 전체 요금제 목록 로드 (복제 기능용)
        async loadAllCosts() {
            try {
                const result = await backEndApi.energyBillCalc.getCostList({});
                if (result.status === 200) {
                    this.allCosts = result.data || [];
                }
            } catch (error) {
                console.error("요금제 목록 조회 실패:", error);
                // 복제 기능은 선택사항이므로 에러를 조용히 처리
            }
        },

        // 선택한 요금제의 단가 정보 복제
        async copyCostDetails() {
            if (!this.selectedCopyCostIdx) {
                await this.alertWarning("복제할 요금제를 선택해주세요.");
                return;
            }

            const selectedCost = this.compatibleCosts.find((c) => c.costIdx === this.selectedCopyCostIdx);
            if (!selectedCost) {
                await this.alertWarning("선택한 요금제를 찾을 수 없습니다.");
                return;
            }

            // 확인 다이얼로그
            const confirm = await this.alertConfirmWarning(
                `'${selectedCost.costName}' 요금제의 단가 정보를 복제하시겠습니까?`,
                "기존 단가 정보는 모두 덮어씌워집니다.",
            );
            if (!confirm.value) {
                return;
            }

            try {
                // 선택한 요금제의 상세 정보 조회
                const result = await backEndApi.energyBillCalc.getCost(this.selectedCopyCostIdx);
                if (result.status === 200) {
                    const sourceDetails = result.data.details || [];
                    if (sourceDetails.length === 0) {
                        await this.alertWarning("복제할 단가 정보가 없습니다.");
                        return;
                    }

                    // 복제한 데이터를 현재 요금제에 적용
                    this.processDetails(sourceDetails);
                    await this.alertNoti("단가 정보가 복제되었습니다. 저장 버튼을 눌러 저장해주세요.");
                } else {
                    await this.alertWarning(result.data.message || "단가 정보를 불러오는데 실패했습니다.");
                }
            } catch (error) {
                console.error("단가 정보 복제 실패:", error);
                await this.alertWarning("단가 정보를 복제하는데 실패했습니다.");
            }
        },

        /** KEPCO 기준 계절별 주중 시간대·부하 (시간은 시, hourEnd 미포함). 계절×시간대×요일 선택 시 사용. */
        getKepcoWeekdayBlocks(seasonCode) {
            const base = (arr) =>
                arr.map((a, i) => ({
                    id: Date.now() + Math.random() + i,
                    hourStart: a[0],
                    hourEnd: a[1],
                    timeSlotCode: a[2],
                    unitPrice: 0,
                    seasonCode: seasonCode,
                    dayType: "WEEKDAY",
                    selected: false,
                }));

            if (seasonCode === "WINTER") {
                // 겨울철(11~2월): 경부하 22~08, 중간 08~09/12~16/19~22, 최대 09~12/16~19
                return base([
                    [22, 24, "LOW"],
                    [0, 8, "LOW"],
                    [8, 9, "MID"],
                    [12, 16, "MID"],
                    [19, 22, "MID"],
                    [9, 12, "PEAK"],
                    [16, 19, "PEAK"],
                ]);
            }
            // 여름철(SUMMER), 봄·가을(MID): 경부하 22~08, 중간 08~11/12~13/18~22, 최대 11~12/13~18
            return base([
                [22, 24, "LOW"],
                [0, 8, "LOW"],
                [8, 11, "MID"],
                [12, 13, "MID"],
                [18, 22, "MID"],
                [11, 12, "PEAK"],
                [13, 18, "PEAK"],
            ]);
        },

        // 기본 timeSlot 초기화 (3차원, 2차원, 1차원)
        initializeDefaultTimeSlots() {
            const hasSeason = this.costInfo.hasSeason === 1;
            const hasDayType = this.costInfo.hasDayType === 1;

            const seasons = hasSeason ? this.seasonOptions : [{ code: null }];

            if (hasSeason && hasDayType) {
                // 3차원(계절×시간대×요일): 주중만 KEPCO 기준으로 생성, 주말/공휴일은 fill에서 자동 생성
                seasons.forEach((season) => {
                    const keyW = `${season.code}_WEEKDAY`;
                    this.timeSlotsByGroup[keyW] = this.getKepcoWeekdayBlocks(season.code);
                });
                this.fillWeekendFromWeekdayIfEmpty();
                this.fillHolidayFromWeekdayIfEmpty();
                return;
            }

            // 공휴일(HOLIDAY)은 fill에서 채우므로 제외
            const dayTypes = hasDayType ? this.dayTypeOptions.filter((d) => d.code !== "HOLIDAY") : [{ code: null }];

            seasons.forEach((season) => {
                dayTypes.forEach((dayType) => {
                    let key;
                    if (hasSeason) {
                        key = season.code;
                    } else if (hasDayType) {
                        key = dayType.code;
                    } else {
                        key = "DEFAULT";
                    }

                    this.timeSlotsByGroup[key] = [
                        {
                            id: Date.now() + Math.random(),
                            hourStart: 0,
                            hourEnd: 7,
                            timeSlotCode: "LOW",
                            unitPrice: 0,
                            seasonCode: hasSeason ? season.code : null,
                            dayType: hasDayType ? dayType.code : null,
                            selected: false,
                        },
                        {
                            id: Date.now() + Math.random() + 1,
                            hourStart: 7,
                            hourEnd: 16,
                            timeSlotCode: "PEAK",
                            unitPrice: 0,
                            seasonCode: hasSeason ? season.code : null,
                            dayType: hasDayType ? dayType.code : null,
                            selected: false,
                        },
                        {
                            id: Date.now() + Math.random() + 2,
                            hourStart: 16,
                            hourEnd: 24,
                            timeSlotCode: "MID",
                            unitPrice: 0,
                            seasonCode: hasSeason ? season.code : null,
                            dayType: hasDayType ? dayType.code : null,
                            selected: false,
                        },
                    ];
                });
            });

            if (hasDayType) this.fillHolidayFromWeekdayIfEmpty();
        },

        /** 주말(토요일): 최대부하+중부하 구간을 하나의 중부하 블록으로 채움. 단가는 주중 중부하에서 자동 반영. */
        /** 주말 = 주중에서 최대부하만 중부하로 바꿈. 경부하는 그대로, 중·최대부하 시간대는 합쳐서 하나의 MID 블록으로 표시. */
        fillWeekendFromWeekdayIfEmpty() {
            if (this.costInfo.hasDayType !== 1 || this.costInfo.hasSeason !== 1) return;
            this.seasonOptions.forEach((season) => {
                const keyWe = `${season.code}_WEEKEND`;
                const existing = this.timeSlotsByGroup[keyWe];
                if (!existing || existing.length === 0) {
                    const weekdaySlots = this.getTimeSlots(season.code, "WEEKDAY");
                    const lowBlocks = weekdaySlots
                        .filter((b) => b.timeSlotCode === "LOW")
                        .map((b) => ({
                            id: Date.now() + Math.random(),
                            hourStart: b.hourStart,
                            hourEnd: b.hourEnd,
                            timeSlotCode: "LOW",
                            unitPrice: b.unitPrice != null && b.unitPrice !== "" ? parseFloat(b.unitPrice) : 0,
                            seasonCode: season.code,
                            dayType: "WEEKEND",
                            selected: false,
                        }));
                    const midPeak = weekdaySlots.filter((b) => b.timeSlotCode === "MID" || b.timeSlotCode === "PEAK");
                    const midBlock = weekdaySlots.find((b) => b.timeSlotCode === "MID");
                    const midPrice =
                        midBlock && midBlock.unitPrice != null && midBlock.unitPrice !== ""
                            ? parseFloat(midBlock.unitPrice)
                            : 0;
                    const mergedMid = {
                        id: Date.now() + Math.random(),
                        hourStart: midPeak.length ? Math.min(...midPeak.map((b) => b.hourStart)) : 8,
                        hourEnd: midPeak.length ? Math.max(...midPeak.map((b) => b.hourEnd)) : 22,
                        timeSlotCode: "MID",
                        unitPrice: midPrice,
                        seasonCode: season.code,
                        dayType: "WEEKEND",
                        selected: false,
                    };
                    const weekendSlots = [...lowBlocks, mergedMid].sort((a, b) => a.hourStart - b.hourStart);
                    this.$set(this.timeSlotsByGroup, keyWe, weekendSlots);
                }
            });
        },

        /** 공휴일이 비어 있을 때만 주중 경부하 단가로 자동 반영. 사용자가 수정 가능. */
        fillHolidayFromWeekdayIfEmpty() {
            if (this.costInfo.hasDayType !== 1) return;

            if (this.costInfo.hasSeason === 1) {
                this.seasonOptions.forEach((season) => {
                    const keyH = `${season.code}_HOLIDAY`;
                    const existing = this.timeSlotsByGroup[keyH];
                    if (!existing || existing.length === 0) {
                        const weekdaySlots = this.getTimeSlots(season.code, "WEEKDAY");
                        const lowBlock = weekdaySlots.find((b) => b.timeSlotCode === "LOW");
                        const unitPrice =
                            lowBlock && lowBlock.unitPrice != null && lowBlock.unitPrice !== ""
                                ? parseFloat(lowBlock.unitPrice)
                                : 0;
                        this.$set(this.timeSlotsByGroup, keyH, [
                            {
                                id: Date.now() + Math.random(),
                                hourStart: 0,
                                hourEnd: 24,
                                timeSlotCode: "LOW",
                                unitPrice,
                                seasonCode: season.code,
                                dayType: "HOLIDAY",
                                selected: false,
                            },
                        ]);
                    }
                });
            } else {
                const existing = this.timeSlotsByGroup["HOLIDAY"];
                if (!existing || existing.length === 0) {
                    const weekdaySlots = this.getTimeSlots2DDay("WEEKDAY") || [];
                    const lowBlock = weekdaySlots.find((b) => b.timeSlotCode === "LOW");
                    const unitPrice =
                        lowBlock && lowBlock.unitPrice != null && lowBlock.unitPrice !== ""
                            ? parseFloat(lowBlock.unitPrice)
                            : 0;
                    this.$set(this.timeSlotsByGroup, "HOLIDAY", [
                        {
                            id: Date.now() + Math.random(),
                            hourStart: 0,
                            hourEnd: 24,
                            timeSlotCode: "LOW",
                            unitPrice,
                            dayType: "HOLIDAY",
                            selected: false,
                        },
                    ]);
                }
            }
        },

        // 1D, 2D 기본 details 초기화
        initializeSimpleDetails() {
            const seasons = this.costInfo.hasSeason === 1 ? this.seasonOptions : [{ code: null }];
            const timeSlots = this.costInfo.hasTimeSlot === 1 ? this.timeSlotOptions : [{ code: null }];
            const dayTypes = this.costInfo.hasDayType === 1 ? this.dayTypeOptions : [{ code: null }];

            seasons.forEach((season) => {
                timeSlots.forEach((timeSlot) => {
                    dayTypes.forEach((dayType) => {
                        this.simpleDetails.push({
                            id: Date.now() + Math.random(),
                            seasonCode: season.code,
                            timeSlotCode: timeSlot.code,
                            dayType: dayType.code,
                            unitPrice: 0,
                        });
                    });
                });
            });
        },

        // 특정 시즌/요일의 timeSlots 가져오기
        getTimeSlots(seasonCode, dayType) {
            const key = `${seasonCode}_${dayType}`;
            return this.timeSlotsByGroup[key] || [];
        },

        // ==================== 3D (계절 × 시간 × 요일) 메서드 ====================
        addTimeSlotBlock3D(seasonCode, dayType) {
            const key = `${seasonCode}_${dayType}`;
            const slots = [...this.getTimeSlots(seasonCode, dayType)]; // 복사본 생성
            const lastHourEnd = slots.length > 0 ? slots[slots.length - 1].hourEnd : 0;
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: lastHourEnd,
                hourEnd: Math.min(lastHourEnd + 8, 24),
                timeSlotCode: "LOW",
                unitPrice: 0,
                seasonCode: seasonCode,
                dayType: dayType,
                selected: false,
            };
            slots.push(newBlock);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, key, [...slots]);
            // 테이블 재렌더링을 위한 강제 업데이트
            this.$forceUpdate();
        },
        splitTimeSlotBlock3D(seasonCode, dayType, index) {
            const key = `${seasonCode}_${dayType}`;
            const slots = [...this.getTimeSlots(seasonCode, dayType)]; // 복사본 생성
            const block = slots[index];

            // 최소 2시간 이상이어야 분할 가능
            if (block.hourEnd - block.hourStart < 2) {
                this.alertWarning("시간대가 너무 짧아 분할할 수 없습니다. (최소 2시간 필요)");
                return;
            }

            // 중간 시간 계산
            const midHour = Math.floor((block.hourStart + block.hourEnd) / 2);

            // 새로운 블록 생성 (원본 블록의 속성 복사)
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: midHour,
                hourEnd: block.hourEnd,
                timeSlotCode: block.timeSlotCode,
                unitPrice: block.unitPrice,
                seasonCode: seasonCode,
                dayType: dayType,
                selected: false,
            };

            // 원본 블록의 종료 시간을 중간 시간으로 변경
            block.hourEnd = midHour;

            // 새 블록을 원본 블록 다음에 삽입
            slots.splice(index + 1, 0, newBlock);

            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, key, [...slots]);
            // 테이블 재렌더링을 위한 강제 업데이트
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        deleteTimeSlotBlock3D(seasonCode, dayType, index) {
            const key = `${seasonCode}_${dayType}`;
            const slots = [...this.getTimeSlots(seasonCode, dayType)]; // 복사본 생성
            if (slots.length <= 1) {
                this.alertWarning("최소 1개의 시간대가 필요합니다.");
                return;
            }
            slots.splice(index, 1);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, key, [...slots]);
            // 테이블 재렌더링을 위한 강제 업데이트
            this.$forceUpdate();
        },
        onUnitPriceChange3D(seasonCode, dayType, changedBlock) {
            const key = `${seasonCode}_${dayType}`;
            const slots = this.getTimeSlots(seasonCode, dayType);
            slots.forEach((block) => {
                if (block.id !== changedBlock.id && block.timeSlotCode === changedBlock.timeSlotCode) {
                    block.unitPrice = changedBlock.unitPrice;
                }
            });
            this.$set(this.timeSlotsByGroup, key, [...slots]);
            // 3차원 요일별: 주중 단가 변경 시 주말(MID)·공휴일(LOW) 단가 자동 반영
            if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1 && dayType === "WEEKDAY") {
                this.syncDerivedDayTypesFromWeekday(seasonCode);
            }
        },
        /** 주중 단가 변경 시 해당 계절의 주말(경부하→경부하, 중·최대부하→중부하)·공휴일(경부하) 단가 자동 반영 */
        syncDerivedDayTypesFromWeekday(seasonCode) {
            const weekdaySlots = this.getTimeSlots(seasonCode, "WEEKDAY");
            const lowBlock = weekdaySlots.find((b) => b.timeSlotCode === "LOW");
            const midBlock = weekdaySlots.find((b) => b.timeSlotCode === "MID");
            const lowPrice =
                lowBlock && lowBlock.unitPrice != null && lowBlock.unitPrice !== ""
                    ? parseFloat(lowBlock.unitPrice)
                    : 0;
            const midPrice =
                midBlock && midBlock.unitPrice != null && midBlock.unitPrice !== ""
                    ? parseFloat(midBlock.unitPrice)
                    : 0;

            const keyH = `${seasonCode}_HOLIDAY`;
            const holidaySlots = this.getTimeSlots(seasonCode, "HOLIDAY");
            if (holidaySlots.length > 0) {
                holidaySlots.forEach((b) => {
                    b.unitPrice = lowPrice;
                });
                this.$set(this.timeSlotsByGroup, keyH, [...holidaySlots]);
            }

            const keyWe = `${seasonCode}_WEEKEND`;
            const weekendSlots = this.getTimeSlots(seasonCode, "WEEKEND");
            if (weekendSlots.length > 0) {
                weekendSlots.forEach((b) => {
                    b.unitPrice = b.timeSlotCode === "LOW" ? lowPrice : midPrice;
                });
                this.$set(this.timeSlotsByGroup, keyWe, [...weekendSlots]);
            }
        },

        // 특정 시간에 해당하는 timeSlot 찾기
        getTimeSlotAtHour(seasonCode, dayType, hour) {
            const slots = this.getTimeSlots(seasonCode, dayType);
            return slots.find((slot) => hour >= slot.hourStart && hour < slot.hourEnd);
        },

        // 해당 시간이 timeSlot의 시작 시간인지
        isTimeSlotStart(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            return slot && slot.hourStart === hour;
        },

        // rowspan 계산
        getTimeSlotRowspan(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return 1;
            return slot.hourEnd - slot.hourStart;
        },

        // 블록의 종료 시간 (표시용)
        getTimeSlotEndHour(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return hour;
            // hourEnd는 미포함이므로 표시할 때는 -1을 해서 표시
            // 예: hourEnd가 7이면 "6시 59분"까지를 의미하므로 "6시"로 표시
            return slot.hourEnd - 1;
        },

        // 블록의 종료 시간 표시 형식 (0시 ~ 7시 형식)
        getTimeSlotEndHourDisplay(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return `${hour}시`;
            // hourEnd가 7이면 "7시"로 표시 (실제로는 06:59까지이지만 표시는 7시)
            // hourEnd가 24이면 "24시"로 표시
            return `${slot.hourEnd}시`;
        },

        // 부하 타입 이름
        getLoadTypeName(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return "";

            const names = {
                LOW: "경부하",
                MID: "중부하",
                PEAK: "최대부하",
            };
            return names[slot.timeSlotCode] || "";
        },

        // 단위요금
        getTimeSlotPrice(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            return slot ? slot.unitPrice : 0;
        },

        // 행 클래스 (선택 상태 등)
        getHourRowClass(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            return {
                "selected-block": slot && slot.selected,
                "block-start": this.isTimeSlotStart(seasonCode, dayType, hour),
            };
        },

        // 부하타입 셀 클래스 (색상)
        getLoadTypeClass(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return "";

            return {
                "load-low": slot.timeSlotCode === "LOW",
                "load-mid": slot.timeSlotCode === "MID",
                "load-peak": slot.timeSlotCode === "PEAK",
            };
        },

        // timeSlot 선택
        selectTimeSlot(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return;

            // 같은 블록을 다시 클릭하면 선택 해제
            if (this.selectedSlot && this.selectedSlot.id === slot.id) {
                this.selectedSlot.selected = false;
                this.selectedSlot = null;
                return;
            }

            // 이전 선택 해제
            if (this.selectedSlot) {
                this.selectedSlot.selected = false;
            }

            // 새 블록 선택
            slot.selected = true;
            this.selectedSlot = slot;
        },

        // 블록 편집 패널 닫기
        closeBlockEditor() {
            if (this.selectedSlot) {
                this.selectedSlot.selected = false;
                this.selectedSlot = null;
            }
        },

        // ==================== 2D (계절 × 시간) 메서드 ====================
        getTimeSlots2DSeason(seasonCode) {
            return this.timeSlotsByGroup[seasonCode] || [];
        },
        getTimeSlots2D(seasonCode) {
            return this.getTimeSlots2DSeason(seasonCode);
        },
        addTimeSlotBlock2D(seasonCode) {
            const slots = [...this.getTimeSlots2D(seasonCode)]; // 복사본 생성
            const lastHourEnd = slots.length > 0 ? slots[slots.length - 1].hourEnd : 0;
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: lastHourEnd,
                hourEnd: Math.min(lastHourEnd + 8, 24),
                timeSlotCode: "LOW",
                unitPrice: 0,
                seasonCode: seasonCode,
                selected: false,
            };
            slots.push(newBlock);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, seasonCode, [...slots]);
        },
        splitTimeSlotBlock2D(seasonCode, index) {
            const slots = [...this.getTimeSlots2D(seasonCode)]; // 복사본 생성
            const block = slots[index];

            // 최소 2시간 이상이어야 분할 가능
            if (block.hourEnd - block.hourStart < 2) {
                this.alertWarning("시간대가 너무 짧아 분할할 수 없습니다. (최소 2시간 필요)");
                return;
            }

            // 중간 시간 계산
            const midHour = Math.floor((block.hourStart + block.hourEnd) / 2);

            // 새로운 블록 생성 (원본 블록의 속성 복사)
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: midHour,
                hourEnd: block.hourEnd,
                timeSlotCode: block.timeSlotCode,
                unitPrice: block.unitPrice,
                seasonCode: seasonCode,
                selected: false,
            };

            // 원본 블록의 종료 시간을 중간 시간으로 변경
            block.hourEnd = midHour;

            // 새 블록을 원본 블록 다음에 삽입
            slots.splice(index + 1, 0, newBlock);

            // 완전히 새로운 배열로 교체하여 반응성 보장 (화면 즉시 반영)
            this.$set(this.timeSlotsByGroup, seasonCode, [...slots]);
            this.$nextTick(() => this.$forceUpdate());
        },
        deleteTimeSlotBlock2D(seasonCode, index) {
            const slots = [...this.getTimeSlots2D(seasonCode)];
            if (slots.length <= 1) {
                this.alertWarning("최소 1개의 시간대가 필요합니다.");
                return;
            }
            slots.splice(index, 1);
            this.$set(this.timeSlotsByGroup, seasonCode, [...slots]);
            this.$nextTick(() => this.$forceUpdate());
        },
        onUnitPriceChange2D(seasonCode, changedBlock) {
            const slots = this.getTimeSlots2D(seasonCode);
            slots.forEach((block) => {
                if (block.id !== changedBlock.id && block.timeSlotCode === changedBlock.timeSlotCode) {
                    block.unitPrice = changedBlock.unitPrice;
                }
            });
            this.$set(this.timeSlotsByGroup, seasonCode, [...slots]);
        },
        getTimeSlotAtHour2D(seasonCode, hour) {
            const slots = this.getTimeSlots2DSeason(seasonCode);
            return slots.find((slot) => hour >= slot.hourStart && hour < slot.hourEnd);
        },
        isTimeSlotStart2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            return slot && slot.hourStart === hour;
        },
        getTimeSlotRowspan2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            return slot ? slot.hourEnd - slot.hourStart : 1;
        },
        getTimeSlotEndHour2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return hour;
            // hourEnd는 미포함이므로 표시할 때는 -1을 해서 표시
            return slot.hourEnd - 1;
        },
        getTimeSlotEndHourDisplay2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return `${hour}시`;
            // hourEnd가 24이면 "24시"로 표시
            return `${slot.hourEnd}시`;
        },
        getLoadTypeName2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return "";
            const names = { LOW: "경부하", MID: "중부하", PEAK: "최대부하" };
            return names[slot.timeSlotCode] || "";
        },
        getTimeSlotPrice2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            return slot ? slot.unitPrice : 0;
        },
        getHourRowClass2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            return {
                "selected-block": slot && slot.selected,
                "block-start": this.isTimeSlotStart2D(seasonCode, hour),
            };
        },
        getLoadTypeClass2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return "";
            return {
                "load-low": slot.timeSlotCode === "LOW",
                "load-mid": slot.timeSlotCode === "MID",
                "load-peak": slot.timeSlotCode === "PEAK",
            };
        },
        selectTimeSlot2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return;
            if (this.selectedSlot && this.selectedSlot.id === slot.id) {
                this.selectedSlot.selected = false;
                this.selectedSlot = null;
                return;
            }
            if (this.selectedSlot) {
                this.selectedSlot.selected = false;
            }
            slot.selected = true;
            this.selectedSlot = slot;
        },
        async openSplitDialog2D(seasonCode, hour) {
            const slot = this.getTimeSlotAtHour2D(seasonCode, hour);
            if (!slot) return;
            const duration = slot.hourEnd - slot.hourStart;
            if (duration < 2) {
                await this.alertWarning("최소 2시간 이상의 블록만 분할할 수 있습니다.");
                return;
            }
            const splitOptions = {};
            for (let h = slot.hourStart + 1; h < slot.hourEnd; h++) {
                const end1 = h; // 첫 번째 블록의 종료는 분할 지점 h
                const end2 = slot.hourEnd; // 두 번째 블록의 종료
                splitOptions[h] = `${slot.hourStart}시 ~ ${end1}시 / ${h}시 ~ ${end2}시`;
            }
            const { value: splitHour } = await this.$swal({
                title: "분할 지점 선택",
                input: "select",
                inputOptions: splitOptions,
                inputPlaceholder: "분할할 시간을 선택하세요",
                showCancelButton: true,
                confirmButtonText: "분할",
                cancelButtonText: "취소",
            });
            if (!splitHour) return;
            this.splitTimeSlot2D(seasonCode, slot.id, parseInt(splitHour));
        },
        splitTimeSlot2D(seasonCode, slotId, splitHour) {
            const slots = this.timeSlotsByGroup[seasonCode];
            const index = slots.findIndex((s) => s.id === slotId);
            if (index === -1) return;
            const slot = slots[index];
            const slot1 = {
                id: Date.now(),
                hourStart: slot.hourStart,
                hourEnd: splitHour, // splitHour는 미포함 값
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                seasonCode: slot.seasonCode,
                selected: false,
            };
            const slot2 = {
                id: Date.now() + 1,
                hourStart: splitHour,
                hourEnd: slot.hourEnd,
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                seasonCode: slot.seasonCode,
                selected: true,
            };
            slots.splice(index, 1, slot1, slot2);
            this.$set(this.timeSlotsByGroup, seasonCode, [...slots]);
            this.selectedSlot = slot2;
            this.alertNoti("블록이 분할되었습니다.");
        },

        // ==================== 2D (요일 × 시간) 메서드 ====================
        getTimeSlots2DDay(dayType) {
            return this.timeSlotsByGroup[dayType] || [];
        },
        addTimeSlotBlock2DDay(dayType) {
            const slots = [...this.getTimeSlots2DDay(dayType)]; // 복사본 생성
            const lastHourEnd = slots.length > 0 ? slots[slots.length - 1].hourEnd : 0;
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: lastHourEnd,
                hourEnd: Math.min(lastHourEnd + 8, 24),
                timeSlotCode: "LOW",
                unitPrice: 0,
                dayType: dayType,
                selected: false,
            };
            slots.push(newBlock);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, dayType, [...slots]);
        },
        splitTimeSlotBlock2DDay(dayType, index) {
            const slots = [...this.getTimeSlots2DDay(dayType)]; // 복사본 생성
            const block = slots[index];

            // 최소 2시간 이상이어야 분할 가능
            if (block.hourEnd - block.hourStart < 2) {
                this.alertWarning("시간대가 너무 짧아 분할할 수 없습니다. (최소 2시간 필요)");
                return;
            }

            // 중간 시간 계산
            const midHour = Math.floor((block.hourStart + block.hourEnd) / 2);

            // 새로운 블록 생성 (원본 블록의 속성 복사)
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: midHour,
                hourEnd: block.hourEnd,
                timeSlotCode: block.timeSlotCode,
                unitPrice: block.unitPrice,
                dayType: dayType,
                selected: false,
            };

            // 원본 블록의 종료 시간을 중간 시간으로 변경
            block.hourEnd = midHour;

            // 새 블록을 원본 블록 다음에 삽입
            slots.splice(index + 1, 0, newBlock);

            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, dayType, [...slots]);
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        deleteTimeSlotBlock2DDay(dayType, index) {
            const slots = [...this.getTimeSlots2DDay(dayType)]; // 복사본 생성
            if (slots.length <= 1) {
                this.alertWarning("최소 1개의 시간대가 필요합니다.");
                return;
            }
            slots.splice(index, 1);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, dayType, [...slots]);
        },
        onUnitPriceChange2DDay(dayType, changedBlock) {
            const slots = this.getTimeSlots2DDay(dayType);
            slots.forEach((block) => {
                if (block.id !== changedBlock.id && block.timeSlotCode === changedBlock.timeSlotCode) {
                    block.unitPrice = changedBlock.unitPrice;
                }
            });
            this.$set(this.timeSlotsByGroup, dayType, [...slots]);
        },
        getTimeSlotAtHour2DDay(dayType, hour) {
            const slots = this.getTimeSlots2DDay(dayType);
            return slots.find((slot) => hour >= slot.hourStart && hour < slot.hourEnd);
        },
        isTimeSlotStart2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            return slot && slot.hourStart === hour;
        },
        getTimeSlotRowspan2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            return slot ? slot.hourEnd - slot.hourStart : 1;
        },
        getTimeSlotEndHour2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return hour;
            // hourEnd는 미포함이므로 표시할 때는 -1을 해서 표시
            return slot.hourEnd - 1;
        },
        getTimeSlotEndHourDisplay2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return `${hour}시`;
            // hourEnd가 24이면 "24시"로 표시
            return `${slot.hourEnd}시`;
        },
        getLoadTypeName2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return "";
            const names = { LOW: "경부하", MID: "중부하", PEAK: "최대부하" };
            return names[slot.timeSlotCode] || "";
        },
        getTimeSlotPrice2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            return slot ? slot.unitPrice : 0;
        },
        getHourRowClass2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            return {
                "selected-block": slot && slot.selected,
                "block-start": this.isTimeSlotStart2DDay(dayType, hour),
            };
        },
        getLoadTypeClass2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return "";
            return {
                "load-low": slot.timeSlotCode === "LOW",
                "load-mid": slot.timeSlotCode === "MID",
                "load-peak": slot.timeSlotCode === "PEAK",
            };
        },
        selectTimeSlot2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return;
            if (this.selectedSlot && this.selectedSlot.id === slot.id) {
                this.selectedSlot.selected = false;
                this.selectedSlot = null;
                return;
            }
            if (this.selectedSlot) {
                this.selectedSlot.selected = false;
            }
            slot.selected = true;
            this.selectedSlot = slot;
        },
        async openSplitDialog2DDay(dayType, hour) {
            const slot = this.getTimeSlotAtHour2DDay(dayType, hour);
            if (!slot) return;
            const duration = slot.hourEnd - slot.hourStart;
            if (duration < 2) {
                await this.alertWarning("최소 2시간 이상의 블록만 분할할 수 있습니다.");
                return;
            }
            const splitOptions = {};
            for (let h = slot.hourStart + 1; h < slot.hourEnd; h++) {
                const end1 = h; // 첫 번째 블록의 종료는 분할 지점 h
                const end2 = slot.hourEnd; // 두 번째 블록의 종료
                splitOptions[h] = `${slot.hourStart}시 ~ ${end1}시 / ${h}시 ~ ${end2}시`;
            }
            const { value: splitHour } = await this.$swal({
                title: "분할 지점 선택",
                input: "select",
                inputOptions: splitOptions,
                inputPlaceholder: "분할할 시간을 선택하세요",
                showCancelButton: true,
                confirmButtonText: "분할",
                cancelButtonText: "취소",
            });
            if (!splitHour) return;
            this.splitTimeSlot2DDay(dayType, slot.id, parseInt(splitHour));
        },
        splitTimeSlot2DDay(dayType, slotId, splitHour) {
            const slots = this.timeSlotsByGroup[dayType];
            const index = slots.findIndex((s) => s.id === slotId);
            if (index === -1) return;
            const slot = slots[index];
            const slot1 = {
                id: Date.now(),
                hourStart: slot.hourStart,
                hourEnd: splitHour, // splitHour는 미포함 값
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                dayType: slot.dayType,
                selected: false,
            };
            const slot2 = {
                id: Date.now() + 1,
                hourStart: splitHour,
                hourEnd: slot.hourEnd,
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                dayType: slot.dayType,
                selected: true,
            };
            slots.splice(index, 1, slot1, slot2);
            this.$set(this.timeSlotsByGroup, dayType, [...slots]);
            this.selectedSlot = slot2;
            this.alertNoti("블록이 분할되었습니다.");
        },

        // ==================== 1D (시간대만) 메서드 ====================
        getTimeSlots1D() {
            return this.timeSlotsByGroup["DEFAULT"] || [];
        },
        getLoadTypeClass1DByBlock(timeSlotCode) {
            return {
                "load-low": timeSlotCode === "LOW",
                "load-mid": timeSlotCode === "MID",
                "load-peak": timeSlotCode === "PEAK",
            };
        },
        addTimeSlotBlock1D() {
            const slots = [...this.getTimeSlots1D()]; // 복사본 생성
            // 마지막 블록의 hourEnd를 새로운 블록의 hourStart로 사용
            const lastHourEnd = slots.length > 0 ? slots[slots.length - 1].hourEnd : 0;
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: lastHourEnd,
                hourEnd: Math.min(lastHourEnd + 8, 24), // 기본 8시간, 최대 24시
                timeSlotCode: "LOW",
                unitPrice: 0,
                selected: false,
            };
            slots.push(newBlock);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, "DEFAULT", [...slots]);
        },
        splitTimeSlotBlock1D(index) {
            const slots = [...this.getTimeSlots1D()]; // 복사본 생성
            const block = slots[index];

            // 최소 2시간 이상이어야 분할 가능
            if (block.hourEnd - block.hourStart < 2) {
                this.alertWarning("시간대가 너무 짧아 분할할 수 없습니다. (최소 2시간 필요)");
                return;
            }

            // 중간 시간 계산
            const midHour = Math.floor((block.hourStart + block.hourEnd) / 2);

            // 새로운 블록 생성 (원본 블록의 속성 복사)
            const newBlock = {
                id: Date.now() + Math.random(),
                hourStart: midHour,
                hourEnd: block.hourEnd,
                timeSlotCode: block.timeSlotCode,
                unitPrice: block.unitPrice,
                selected: false,
            };

            // 원본 블록의 종료 시간을 중간 시간으로 변경
            block.hourEnd = midHour;

            // 새 블록을 원본 블록 다음에 삽입
            slots.splice(index + 1, 0, newBlock);

            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, "DEFAULT", [...slots]);
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        deleteTimeSlotBlock1D(index) {
            const slots = [...this.getTimeSlots1D()]; // 복사본 생성
            if (slots.length <= 1) {
                this.alertWarning("최소 1개의 시간대가 필요합니다.");
                return;
            }
            slots.splice(index, 1);
            // 완전히 새로운 배열로 교체하여 반응성 보장
            this.$set(this.timeSlotsByGroup, "DEFAULT", [...slots]);
        },
        onUnitPriceChange1D(changedBlock) {
            // 현재 테이블(1차원) 내에서 동일한 timeSlotCode를 가진 다른 블록들의 단위요금도 함께 변경
            const slots = this.getTimeSlots1D();
            slots.forEach((block) => {
                if (block.id !== changedBlock.id && block.timeSlotCode === changedBlock.timeSlotCode) {
                    block.unitPrice = changedBlock.unitPrice;
                }
            });
            this.$set(this.timeSlotsByGroup, "DEFAULT", [...slots]);
        },
        // 시작시간 옵션 생성 (인접 시간대 참조)
        getStartHourOptions(slots, currentBlock) {
            const options = [{ value: null, label: "미지정" }];

            if (!slots || !currentBlock || currentBlock.hourStart === null || currentBlock.hourStart === undefined) {
                // 시작시간이 미지정이면 0-23시 전체 제공
                for (let hour = 0; hour < 24; hour++) {
                    options.push({
                        value: hour,
                        label: `${hour}시`,
                    });
                }
                return options;
            }

            const sortedSlots = [...slots].sort((a, b) => a.hourStart - b.hourStart);
            const actualIndex = sortedSlots.findIndex((s) => s.id === currentBlock.id);
            const currentEnd = currentBlock.hourEnd || 24;

            // 최소 시작시간: 이전 시간대가 소멸하지 않을 때까지
            let minStart = 0; // 기본값 (첫 번째 시간대인 경우)
            if (actualIndex > 0) {
                const prevBlock = sortedSlots[actualIndex - 1];
                if (prevBlock && prevBlock.id !== currentBlock.id) {
                    minStart = prevBlock.hourStart + 1; // 이전 시간대 시작시간 + 1
                }
            }

            // 최대 시작시간: 종료시간 - 1 (최소 1시간 크기)
            const maxStart = currentEnd - 1;

            // 실제 최소값은 최대값 이하여야 함
            const actualMinStart = Math.min(minStart, maxStart);

            // 옵션 생성
            for (let hour = actualMinStart; hour <= maxStart; hour++) {
                options.push({
                    value: hour,
                    label: `${hour}시`,
                });
            }

            return options;
        },
        // 1D용 시작시간 옵션
        getStartHourOptions1D(currentBlock) {
            const slots = this.getTimeSlots1D();
            return this.getStartHourOptions(slots, currentBlock);
        },
        // 1D용 종료시간 옵션 생성 (인접 시간대 참조)
        getEndHourOptions1D(currentBlock) {
            const slots = this.getTimeSlots1D();
            return this.getEndHourOptionsCommon(slots, currentBlock);
        },
        // 1D용 시작시간 변경 핸들러
        onStartHourChange1D(currentBlock, currentIndex) {
            const slots = this.getTimeSlots1D();
            this.onStartHourChangeCommon(slots, currentBlock, currentIndex, "DEFAULT");
        },
        // 1D용 종료시간 변경 핸들러
        onEndHourChange1D(currentBlock, currentIndex) {
            const slots = this.getTimeSlots1D();
            this.onEndHourChangeCommon(slots, currentBlock, currentIndex, "DEFAULT");
        },
        // 공통: 종료시간 옵션 생성 (인접 시간대 참조)
        getEndHourOptionsCommon(slots, currentBlock) {
            const options = [{ value: null, label: "미지정" }];

            // 시작시간이 null이 아니고 숫자인 경우에만 시간 옵션 제공
            if (
                currentBlock.hourStart === null ||
                currentBlock.hourStart === undefined ||
                typeof currentBlock.hourStart !== "number"
            ) {
                return options;
            }

            const sortedSlots = [...slots].sort((a, b) => a.hourStart - b.hourStart);
            const actualIndex = sortedSlots.findIndex((s) => s.id === currentBlock.id);
            const currentStart = currentBlock.hourStart;

            // 최소 종료시간: 시작시간 + 1 (최소 1시간 크기)
            const minEnd = currentStart + 1;

            // 최대 종료시간: 다음 시간대가 소멸하지 않을 때까지
            let maxEnd = 24; // 기본값 (마지막 시간대인 경우)
            if (actualIndex >= 0 && actualIndex < sortedSlots.length - 1) {
                const nextBlock = sortedSlots[actualIndex + 1];
                if (nextBlock && nextBlock.id !== currentBlock.id) {
                    maxEnd = nextBlock.hourEnd - 1; // 다음 시간대 종료시간 - 1
                }
            }

            // 실제 최대값은 최소값 이상이어야 함
            const actualMaxEnd = Math.max(minEnd, maxEnd);

            // 옵션 생성
            for (let hour = minEnd; hour <= actualMaxEnd; hour++) {
                options.push({
                    value: hour,
                    label: hour === 24 ? "24시" : `${hour}시`,
                });
            }

            return options;
        },
        // 공통: 시작시간 변경 핸들러 (인접 시간대 자동 조정)
        onStartHourChangeCommon(slots, currentBlock, _currentIndex, groupKey) {
            // 시작시간이 null이면 종료시간도 null로 초기화
            if (currentBlock.hourStart === null || currentBlock.hourStart === undefined) {
                this.$set(currentBlock, "hourEnd", null);
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
                return;
            }

            // 원본 배열을 복사하고 정렬
            const sortedSlots = [...slots].sort((a, b) => a.hourStart - b.hourStart);
            const actualIndex = sortedSlots.findIndex((s) => s.id === currentBlock.id);

            if (actualIndex === -1) {
                console.warn("Block not found in sorted slots");
                return;
            }

            // sortedSlots에서 실제 블록 참조 가져오기
            const blockInSorted = sortedSlots[actualIndex];
            const newStart = blockInSorted.hourStart;

            // 1. 이전 시간대의 종료시간을 현재 시간대의 시작시간으로 자동 조정
            if (actualIndex > 0) {
                const prevBlock = sortedSlots[actualIndex - 1];
                if (prevBlock && prevBlock.id !== blockInSorted.id) {
                    // 이전 시간대가 최소 1시간을 유지할 수 있는지 확인
                    const minPrevEnd = prevBlock.hourStart + 1;
                    if (newStart >= minPrevEnd) {
                        // 이전 시간대의 종료시간을 현재 시작시간으로 조정
                        prevBlock.hourEnd = newStart;
                    } else {
                        // 이전 시간대가 최소 크기를 유지할 수 없으면 현재 시작시간을 조정
                        blockInSorted.hourStart = minPrevEnd;
                    }
                }
            }

            // 2. 현재 시간대의 종료시간이 새로운 시작시간보다 작거나 같으면 조정
            if (blockInSorted.hourEnd <= blockInSorted.hourStart) {
                blockInSorted.hourEnd = Math.min(blockInSorted.hourStart + 1, 24);
            }

            // 3. 종료시간이 다음 시간대 종료시간 - 1보다 크면 조정 (다음 시간대 보호)
            if (actualIndex < sortedSlots.length - 1) {
                const nextBlock = sortedSlots[actualIndex + 1];
                if (nextBlock && nextBlock.id !== blockInSorted.id) {
                    const maxAllowedEnd = nextBlock.hourEnd - 1; // 다음 시간대 종료시간 - 1
                    if (blockInSorted.hourEnd > maxAllowedEnd) {
                        blockInSorted.hourEnd = maxAllowedEnd;
                    }
                }
            }

            // Vue 반응성을 보장하기 위해 전체 배열을 새로 설정
            this.$set(this.timeSlotsByGroup, groupKey, sortedSlots);
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        // 공통: 종료시간 변경 핸들러 (인접 시간대 자동 조정)
        onEndHourChangeCommon(slots, currentBlock, _currentIndex, groupKey) {
            // 종료시간이 null이면 아무것도 하지 않음
            if (currentBlock.hourEnd === null || currentBlock.hourEnd === undefined) {
                return;
            }

            // 원본 배열을 복사하고 정렬
            const sortedSlots = [...slots].sort((a, b) => a.hourStart - b.hourStart);
            const actualIndex = sortedSlots.findIndex((s) => s.id === currentBlock.id);

            if (actualIndex === -1) {
                console.warn("Block not found in sorted slots");
                return;
            }

            // sortedSlots에서 실제 블록 참조 가져오기
            const blockInSorted = sortedSlots[actualIndex];
            const newEnd = blockInSorted.hourEnd;

            // 1. 다음 시간대의 시작시간을 현재 시간대의 종료시간으로 자동 조정
            if (actualIndex < sortedSlots.length - 1) {
                const nextBlock = sortedSlots[actualIndex + 1];
                if (nextBlock && nextBlock.id !== blockInSorted.id) {
                    // 다음 시간대가 최소 1시간을 유지할 수 있는지 확인
                    const maxNextStart = nextBlock.hourEnd - 1;
                    if (newEnd <= maxNextStart) {
                        // 다음 시간대의 시작시간을 현재 종료시간으로 조정
                        nextBlock.hourStart = newEnd;
                    } else {
                        // 다음 시간대가 최소 크기를 유지할 수 없으면 현재 종료시간을 조정
                        blockInSorted.hourEnd = maxNextStart;
                    }
                }
            }

            // 2. 현재 시간대의 시작시간이 새로운 종료시간보다 크거나 같으면 조정
            if (blockInSorted.hourStart >= blockInSorted.hourEnd) {
                blockInSorted.hourStart = Math.max(blockInSorted.hourEnd - 1, 0);
            }

            // 3. 시작시간이 이전 시간대 시작시간 + 1보다 작으면 조정 (이전 시간대 보호)
            if (actualIndex > 0) {
                const prevBlock = sortedSlots[actualIndex - 1];
                if (prevBlock && prevBlock.id !== blockInSorted.id) {
                    const minAllowedStart = prevBlock.hourStart + 1; // 이전 시간대 시작시간 + 1
                    if (blockInSorted.hourStart < minAllowedStart) {
                        blockInSorted.hourStart = minAllowedStart;
                    }
                }
            }

            // Vue 반응성을 보장하기 위해 전체 배열을 새로 설정
            this.$set(this.timeSlotsByGroup, groupKey, sortedSlots);
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        // 2D용 시작시간 옵션
        getStartHourOptions2D(seasonCode, currentBlock) {
            const slots = this.getTimeSlots2D(seasonCode);
            return this.getStartHourOptions(slots, currentBlock);
        },
        // 2D용 종료시간 옵션
        getEndHourOptions2D(seasonCode, currentBlock) {
            const slots = this.getTimeSlots2D(seasonCode);
            return this.getEndHourOptionsCommon(slots, currentBlock);
        },
        // 2D용 시작시간 변경 핸들러
        onStartHourChange2D(seasonCode, currentBlock, currentIndex) {
            const slots = this.getTimeSlots2D(seasonCode);
            this.onStartHourChangeCommon(slots, currentBlock, currentIndex, seasonCode);
        },
        // 2D용 종료시간 변경 핸들러
        onEndHourChange2D(seasonCode, currentBlock, currentIndex) {
            const slots = this.getTimeSlots2D(seasonCode);
            this.onEndHourChangeCommon(slots, currentBlock, currentIndex, seasonCode);
        },
        // 2DDay용 시작시간 옵션
        getStartHourOptions2DDay(dayType, currentBlock) {
            const slots = this.getTimeSlots2DDay(dayType);
            return this.getStartHourOptions(slots, currentBlock);
        },
        // 2DDay용 종료시간 옵션
        getEndHourOptions2DDay(dayType, currentBlock) {
            const slots = this.getTimeSlots2DDay(dayType);
            return this.getEndHourOptionsCommon(slots, currentBlock);
        },
        // 2DDay용 시작시간 변경 핸들러
        onStartHourChange2DDay(dayType, currentBlock, currentIndex) {
            const slots = this.getTimeSlots2DDay(dayType);
            this.onStartHourChangeCommon(slots, currentBlock, currentIndex, dayType);
        },
        // 2DDay용 종료시간 변경 핸들러
        onEndHourChange2DDay(dayType, currentBlock, currentIndex) {
            const slots = this.getTimeSlots2DDay(dayType);
            this.onEndHourChangeCommon(slots, currentBlock, currentIndex, dayType);
        },
        // 3D용 시작시간 옵션
        getStartHourOptions3D(seasonCode, dayType, currentBlock) {
            const slots = this.getTimeSlots(seasonCode, dayType);
            return this.getStartHourOptions(slots, currentBlock);
        },
        // 3D용 종료시간 옵션
        getEndHourOptions3D(seasonCode, dayType, currentBlock) {
            const slots = this.getTimeSlots(seasonCode, dayType);
            return this.getEndHourOptionsCommon(slots, currentBlock);
        },
        // 3D용 시작시간 변경 핸들러
        onStartHourChange3D(seasonCode, dayType, currentBlock, currentIndex) {
            const key = `${seasonCode}_${dayType}`;
            const slots = this.getTimeSlots(seasonCode, dayType);
            this.onStartHourChangeCommon(slots, currentBlock, currentIndex, key);
        },
        // 3D용 종료시간 변경 핸들러
        onEndHourChange3D(seasonCode, dayType, currentBlock, currentIndex) {
            const key = `${seasonCode}_${dayType}`;
            const slots = this.getTimeSlots(seasonCode, dayType);
            this.onEndHourChangeCommon(slots, currentBlock, currentIndex, key);
        },
        getTimeSlotAtHour1D(hour) {
            const slots = this.getTimeSlots1D();
            return slots.find((slot) => hour >= slot.hourStart && hour < slot.hourEnd);
        },
        isTimeSlotStart1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            return slot && slot.hourStart === hour;
        },
        getTimeSlotRowspan1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            return slot ? slot.hourEnd - slot.hourStart : 1;
        },
        getTimeSlotEndHour1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return hour;
            // hourEnd는 미포함이므로 표시할 때는 -1을 해서 표시
            return slot.hourEnd - 1;
        },
        getTimeSlotEndHourDisplay1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return `${hour}시`;
            // hourEnd가 24이면 "24시"로 표시
            return `${slot.hourEnd}시`;
        },
        getLoadTypeName1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return "";
            const names = { LOW: "경부하", MID: "중부하", PEAK: "최대부하" };
            return names[slot.timeSlotCode] || "";
        },
        getTimeSlotPrice1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            return slot ? slot.unitPrice : 0;
        },
        getHourRowClass1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            return {
                "selected-block": slot && slot.selected,
                "block-start": this.isTimeSlotStart1D(hour),
            };
        },
        getLoadTypeClass1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return "";
            return {
                "load-low": slot.timeSlotCode === "LOW",
                "load-mid": slot.timeSlotCode === "MID",
                "load-peak": slot.timeSlotCode === "PEAK",
            };
        },
        selectTimeSlot1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return;
            if (this.selectedSlot && this.selectedSlot.id === slot.id) {
                this.selectedSlot.selected = false;
                this.selectedSlot = null;
                return;
            }
            if (this.selectedSlot) {
                this.selectedSlot.selected = false;
            }
            slot.selected = true;
            this.selectedSlot = slot;
        },
        async openSplitDialog1D(hour) {
            const slot = this.getTimeSlotAtHour1D(hour);
            if (!slot) return;
            const duration = slot.hourEnd - slot.hourStart;
            if (duration < 2) {
                await this.alertWarning("최소 2시간 이상의 블록만 분할할 수 있습니다.");
                return;
            }
            const splitOptions = {};
            for (let h = slot.hourStart + 1; h < slot.hourEnd; h++) {
                const end1 = h; // 첫 번째 블록의 종료는 분할 지점 h
                const end2 = slot.hourEnd; // 두 번째 블록의 종료
                splitOptions[h] = `${slot.hourStart}시 ~ ${end1}시 / ${h}시 ~ ${end2}시`;
            }
            const { value: splitHour } = await this.$swal({
                title: "분할 지점 선택",
                input: "select",
                inputOptions: splitOptions,
                inputPlaceholder: "분할할 시간을 선택하세요",
                showCancelButton: true,
                confirmButtonText: "분할",
                cancelButtonText: "취소",
            });
            if (!splitHour) return;
            this.splitTimeSlot1D(slot.id, parseInt(splitHour));
        },
        splitTimeSlot1D(slotId, splitHour) {
            const slots = this.timeSlotsByGroup["DEFAULT"];
            const index = slots.findIndex((s) => s.id === slotId);
            if (index === -1) return;
            const slot = slots[index];
            const slot1 = {
                id: Date.now(),
                hourStart: slot.hourStart,
                hourEnd: splitHour, // splitHour는 미포함 값
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                selected: false,
            };
            const slot2 = {
                id: Date.now() + 1,
                hourStart: splitHour,
                hourEnd: slot.hourEnd,
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                selected: true,
            };
            slots.splice(index, 1, slot1, slot2);
            this.$set(this.timeSlotsByGroup, "DEFAULT", [...slots]);
            this.selectedSlot = slot2;
            this.alertNoti("블록이 분할되었습니다.");
        },

        // 분할 다이얼로그 열기 (3차원용)
        async openSplitDialog(seasonCode, dayType, hour) {
            const slot = this.getTimeSlotAtHour(seasonCode, dayType, hour);
            if (!slot) return;

            const duration = slot.hourEnd - slot.hourStart;

            if (duration < 2) {
                await this.alertWarning("최소 2시간 이상의 블록만 분할할 수 있습니다.");
                return;
            }

            // 분할 지점 선택 옵션 생성
            const splitOptions = {};
            for (let h = slot.hourStart + 1; h < slot.hourEnd; h++) {
                const end1 = h; // 첫 번째 블록의 종료는 분할 지점 h
                const end2 = slot.hourEnd; // 두 번째 블록의 종료
                splitOptions[h] = `${slot.hourStart}시 ~ ${end1}시 / ${h}시 ~ ${end2}시`;
            }

            // SweetAlert로 분할 지점 선택
            const { value: splitHour } = await this.$swal({
                title: "분할 지점 선택",
                input: "select",
                inputOptions: splitOptions,
                inputPlaceholder: "분할할 시간을 선택하세요",
                showCancelButton: true,
                confirmButtonText: "분할",
                cancelButtonText: "취소",
            });

            if (!splitHour) return;

            this.splitTimeSlot(seasonCode, dayType, slot.id, parseInt(splitHour));
        },

        // timeSlot 분할
        splitTimeSlot(seasonCode, dayType, slotId, splitHour) {
            const key = `${seasonCode}_${dayType}`;
            const slots = this.timeSlotsByGroup[key];
            const index = slots.findIndex((s) => s.id === slotId);

            if (index === -1) return;

            const slot = slots[index];

            // 새로운 2개 블록 생성
            const slot1 = {
                id: Date.now(),
                hourStart: slot.hourStart,
                hourEnd: splitHour, // splitHour는 미포함 값
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                seasonCode: slot.seasonCode,
                dayType: slot.dayType,
                selected: false,
            };

            const slot2 = {
                id: Date.now() + 1,
                hourStart: splitHour,
                hourEnd: slot.hourEnd,
                timeSlotCode: slot.timeSlotCode,
                unitPrice: slot.unitPrice,
                seasonCode: slot.seasonCode,
                dayType: slot.dayType,
                selected: true,
            };

            // 기존 블록 제거하고 2개 삽입
            slots.splice(index, 1, slot1, slot2);

            // Vue 반응성 강제 업데이트
            this.$set(this.timeSlotsByGroup, key, [...slots]);

            // 선택 상태 업데이트
            this.selectedSlot = slot2;

            this.alertNoti("블록이 분할되었습니다.");
        },

        // 병합 다이얼로그 열기
        async openMergeDialog() {
            if (!this.selectedSlot) return;

            const slot = this.selectedSlot;
            // timeSlotsByGroup의 키 생성 로직과 동일하게 맞춤
            let key;
            if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1) {
                // 3차원: 계절 × 요일
                key = `${slot.seasonCode}_${slot.dayType}`;
            } else if (this.costInfo.hasSeason === 1) {
                // 2차원: 계절만
                key = slot.seasonCode;
            } else if (this.costInfo.hasDayType === 1) {
                // 2차원: 요일만
                key = slot.dayType;
            } else {
                // 1차원: 시간대만
                key = "DEFAULT";
            }

            const slots = this.timeSlotsByGroup[key];
            if (!slots || slots.length === 0) {
                await this.alertWarning("병합할 블록 정보를 찾을 수 없습니다.");
                return;
            }
            const index = slots.findIndex((s) => s.id === slot.id);

            // 병합 가능한 블록 찾기
            const mergeCandidates = [];

            // 이전 블록 확인 (hourEnd는 미포함이므로 prev.hourEnd === slot.hourStart)
            if (index > 0) {
                const prev = slots[index - 1];
                if (prev.hourEnd === slot.hourStart) {
                    mergeCandidates.push({
                        type: "prev",
                        block: prev,
                        text: `이전 블록과 병합 (${prev.hourStart}시 ~ ${slot.hourEnd}시)`,
                    });
                }
            }

            // 다음 블록 확인 (hourEnd는 미포함이므로 slot.hourEnd === next.hourStart)
            if (index < slots.length - 1) {
                const next = slots[index + 1];
                if (slot.hourEnd === next.hourStart) {
                    mergeCandidates.push({
                        type: "next",
                        block: next,
                        text: `다음 블록과 병합 (${slot.hourStart}시 ~ ${next.hourEnd}시)`,
                    });
                }
            }

            if (mergeCandidates.length === 0) {
                await this.alertWarning("병합 가능한 인접 블록이 없습니다.");
                return;
            }

            // 병합 옵션 선택
            let selectedOption;
            if (mergeCandidates.length === 1) {
                const result = await this.alertConfirmWarning(mergeCandidates[0].text, "병합하시겠습니까?");
                if (result.isConfirmed || result.value) {
                    selectedOption = mergeCandidates[0];
                }
            } else {
                const inputOptions = {};
                mergeCandidates.forEach((opt, idx) => {
                    inputOptions[idx] = opt.text;
                });

                const { value } = await this.$swal({
                    title: "병합 방향 선택",
                    input: "radio",
                    inputOptions: inputOptions,
                    showCancelButton: true,
                    confirmButtonText: "병합",
                    cancelButtonText: "취소",
                });

                if (value !== undefined) {
                    selectedOption = mergeCandidates[parseInt(value)];
                }
            }

            if (!selectedOption) return;

            // 병합 실행
            if (selectedOption.type === "prev") {
                selectedOption.block.hourEnd = slot.hourEnd;
                selectedOption.block.selected = true;
                slots.splice(index, 1);
                this.selectedSlot = selectedOption.block;
            } else {
                slot.hourEnd = selectedOption.block.hourEnd;
                slots.splice(index + 1, 1);
            }

            // Vue 반응성 강제 업데이트
            this.$set(this.timeSlotsByGroup, key, [...slots]);

            this.alertNoti("블록이 병합되었습니다.");
        },

        // timeSlot 삭제
        async deleteTimeSlotBlock() {
            if (!this.selectedSlot) return;

            const slot = this.selectedSlot;
            const key = `${slot.seasonCode}_${slot.dayType}`;
            const slots = this.timeSlotsByGroup[key];

            if (!slots) return;

            // 마지막 블록은 삭제 불가
            if (slots.length === 1) {
                await this.alertWarning("마지막 블록은 삭제할 수 없습니다.");
                return;
            }

            const result = await this.alertConfirmWarning(
                `${slot.hourStart}시 ~ ${slot.hourEnd}시 블록을 삭제하시겠습니까?`,
                "삭제 후 인접 블록이 자동으로 확장됩니다.",
            );

            // SweetAlert2 버전에 따라 isConfirmed 또는 value 확인
            if (!result || (!result.isConfirmed && !result.value)) {
                return;
            }

            const index = slots.findIndex((s) => s.id === slot.id);

            if (index === -1) return;

            // 인접 블록 확장 (삭제 전에 먼저 처리)
            if (index > 0) {
                // 이전 블록이 있으면 이전 블록을 확장
                slots[index - 1].hourEnd = slot.hourEnd;
            } else if (index < slots.length - 1) {
                // 첫 블록이면 다음 블록을 확장
                slots[index + 1].hourStart = slot.hourStart;
            }

            // 블록 삭제
            slots.splice(index, 1);

            // Vue 반응성 강제 업데이트
            this.$set(this.timeSlotsByGroup, key, [...slots]);

            // 선택 해제
            this.selectedSlot = null;

            await this.alertNoti("블록이 삭제되었습니다.");
        },

        // 블록 속성 변경 시 처리 (자동 병합 제거 - 사용자 혼란 방지)
        onSlotPropertyChange() {
            // 자동 병합 로직 제거
            // 사용자가 시간대를 변경할 때 예상치 못한 병합이 발생하는 것을 방지
            // 병합이 필요하면 사용자가 명시적으로 "병합" 버튼을 클릭하도록 함
        },

        // 단가 변경 시 동일한 부하 구분의 다른 구간에도 자동 적용 (모달에서 입력)
        onUnitPriceChange() {
            if (!this.selectedSlot || !this.selectedSlot.timeSlotCode) return;

            const newPrice = this.selectedSlot.unitPrice;
            const targetTimeSlotCode = this.selectedSlot.timeSlotCode;
            const slotSeasonCode = this.selectedSlot.seasonCode;
            const slotDayType = this.selectedSlot.dayType;

            // 구조에 따라 업데이트 범위 결정
            if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1) {
                // 3차원: 계절 × 시간대 × 요일
                // 같은 seasonCode와 dayType을 가진 그룹에서만 업데이트
                const targetKey = `${slotSeasonCode}_${slotDayType}`;
                const slots = this.timeSlotsByGroup[targetKey] || [];
                slots.forEach((slot) => {
                    if (slot.id !== this.selectedSlot.id && slot.timeSlotCode === targetTimeSlotCode) {
                        slot.unitPrice = newPrice;
                    }
                });
            } else if (this.costInfo.hasSeason === 1) {
                // 2차원: 계절 × 시간대
                // 같은 seasonCode를 가진 모든 그룹에서 업데이트
                Object.keys(this.timeSlotsByGroup).forEach((key) => {
                    if (key === slotSeasonCode) {
                        const slots = this.timeSlotsByGroup[key];
                        slots.forEach((slot) => {
                            if (slot.id !== this.selectedSlot.id && slot.timeSlotCode === targetTimeSlotCode) {
                                slot.unitPrice = newPrice;
                            }
                        });
                    }
                });
            } else if (this.costInfo.hasDayType === 1) {
                // 2차원: 시간대 × 요일
                // 같은 dayType을 가진 모든 그룹에서 업데이트
                Object.keys(this.timeSlotsByGroup).forEach((key) => {
                    if (key === slotDayType) {
                        const slots = this.timeSlotsByGroup[key];
                        slots.forEach((slot) => {
                            if (slot.id !== this.selectedSlot.id && slot.timeSlotCode === targetTimeSlotCode) {
                                slot.unitPrice = newPrice;
                            }
                        });
                    }
                });
            } else {
                // 1차원: 시간대만
                // 모든 그룹에서 업데이트
                Object.keys(this.timeSlotsByGroup).forEach((key) => {
                    const slots = this.timeSlotsByGroup[key];
                    slots.forEach((slot) => {
                        if (slot.id !== this.selectedSlot.id && slot.timeSlotCode === targetTimeSlotCode) {
                            slot.unitPrice = newPrice;
                        }
                    });
                });
            }
        },

        // 단가 변경 시 동일한 부하 구분의 다른 구간에도 자동 적용 (simpleDetails에서 입력)
        onSimpleDetailUnitPriceChange(changedDetail) {
            // 시간대 개념이 있는 경우에만 동작
            if (!this.costInfo.hasTimeSlot || !changedDetail.timeSlotCode) return;

            const newPrice = changedDetail.unitPrice;
            const targetTimeSlotCode = changedDetail.timeSlotCode;
            const detailSeasonCode = changedDetail.seasonCode;
            const detailDayType = changedDetail.dayType;

            // 구조에 따라 업데이트 범위 결정
            if (this.costInfo.hasSeason === 1 && this.costInfo.hasDayType === 1) {
                // 3차원: 계절 × 시간대 × 요일
                // 같은 seasonCode와 dayType을 가진 항목에서만 업데이트
                this.simpleDetails.forEach((detail) => {
                    if (
                        detail !== changedDetail &&
                        detail.timeSlotCode === targetTimeSlotCode &&
                        detail.seasonCode === detailSeasonCode &&
                        detail.dayType === detailDayType
                    ) {
                        detail.unitPrice = newPrice;
                    }
                });
            } else if (this.costInfo.hasSeason === 1) {
                // 2차원: 계절 × 시간대
                // 같은 seasonCode를 가진 항목에서만 업데이트
                this.simpleDetails.forEach((detail) => {
                    if (
                        detail !== changedDetail &&
                        detail.timeSlotCode === targetTimeSlotCode &&
                        detail.seasonCode === detailSeasonCode
                    ) {
                        detail.unitPrice = newPrice;
                    }
                });
            } else if (this.costInfo.hasDayType === 1) {
                // 2차원: 시간대 × 요일
                // 같은 dayType을 가진 항목에서만 업데이트
                this.simpleDetails.forEach((detail) => {
                    if (
                        detail !== changedDetail &&
                        detail.timeSlotCode === targetTimeSlotCode &&
                        detail.dayType === detailDayType
                    ) {
                        detail.unitPrice = newPrice;
                    }
                });
            } else {
                // 1차원: 시간대만
                // 모든 항목에서 업데이트
                this.simpleDetails.forEach((detail) => {
                    if (detail !== changedDetail && detail.timeSlotCode === targetTimeSlotCode) {
                        detail.unitPrice = newPrice;
                    }
                });
            }
        },

        // 누진제 구간 추가
        addProgressiveDetail() {
            const lastDetail = this.progressiveDetails[this.progressiveDetails.length - 1];
            const newFrom = lastDetail && lastDetail.usageTo ? lastDetail.usageTo + 1 : 0;

            this.progressiveDetails.push({
                id: Date.now(),
                usageFrom: newFrom,
                usageTo: null,
                unitPrice: 0,
                baseFeeValue: 0,
            });
        },

        // 누진제 구간 삭제
        deleteProgressiveDetail(index) {
            if (this.progressiveDetails.length <= 1) {
                this.alertWarning("최소 1개의 구간은 유지되어야 합니다.");
                return;
            }
            this.progressiveDetails.splice(index, 1);
        },

        // 저장
        async handleSave() {
            // 검증
            if (this.isProgressiveType) {
                for (let i = 0; i < this.progressiveDetails.length; i++) {
                    const detail = this.progressiveDetails[i];

                    // usageFrom 필수 검증
                    if (detail.usageFrom === null || detail.usageFrom === undefined || detail.usageFrom === "") {
                        await this.alertWarning(`구간 ${i + 1}의 시작 사용량(usageFrom)을 입력해주세요.`);
                        return;
                    }

                    // usageTo 검증 (마지막 구간이 아닌 경우 필수)
                    if (i < this.progressiveDetails.length - 1) {
                        if (detail.usageTo === null || detail.usageTo === undefined || detail.usageTo === "") {
                            await this.alertWarning(`구간 ${i + 1}의 종료 사용량(usageTo)을 입력해주세요.`);
                            return;
                        }
                    }

                    // usageTo > usageFrom 검증
                    if (detail.usageTo !== null && detail.usageTo !== undefined && detail.usageTo !== "") {
                        if (detail.usageTo <= detail.usageFrom) {
                            await this.alertWarning(`구간 ${i + 1}: 종료 사용량은 시작 사용량보다 커야 합니다.`);
                            return;
                        }
                    }

                    // unitPrice 필수 검증
                    if (detail.unitPrice <= 0) {
                        await this.alertWarning(`구간 ${i + 1}의 단위요금을 입력해주세요.`);
                        return;
                    }
                }

                // 구간 겹침 검증
                const sorted = [...this.progressiveDetails].sort((a, b) => a.usageFrom - b.usageFrom);
                for (let i = 0; i < sorted.length - 1; i++) {
                    const current = sorted[i];
                    const next = sorted[i + 1];
                    if (current.usageTo !== null && current.usageTo !== undefined && current.usageTo !== "") {
                        if (current.usageTo > next.usageFrom) {
                            await this.alertWarning(`구간이 겹칩니다. 구간 ${i + 1}과 구간 ${i + 2}를 확인해주세요.`);
                            return;
                        }
                    }
                }
            } else if (this.costInfo.hasTimeSlot === 1) {
                // 시간대 개념이 있는 경우: 24시간 커버 검증 및 필수 필드 검증
                for (const key in this.timeSlotsByGroup) {
                    const slots = this.timeSlotsByGroup[key];

                    // 24시간 커버 검증
                    const validation = this.validate24HourCoverage(slots);
                    if (!validation.valid) {
                        await this.alertWarning(`${key}: ${validation.error}`);
                        return;
                    }

                    // timeSlotCode 및 unitPrice 필수 검증
                    for (let i = 0; i < slots.length; i++) {
                        const slot = slots[i];
                        if (!slot.timeSlotCode) {
                            await this.alertWarning(
                                `${key}의 시간대 ${i + 1}: 시간대 코드(timeSlotCode)를 입력해주세요.`,
                            );
                            return;
                        }
                        if (!slot.unitPrice || slot.unitPrice <= 0) {
                            await this.alertWarning(`${key}의 시간대 ${i + 1}: 단위요금을 입력해주세요.`);
                            return;
                        }
                    }
                }
            }

            const payload = {
                costIdx: this.costIdx,
                details: [],
            };

            if (this.isProgressiveType) {
                // 누진제
                payload.details = this.progressiveDetails.map((detail) => ({
                    usageFrom: detail.usageFrom,
                    usageTo: detail.usageTo || null,
                    unitPrice: detail.unitPrice,
                    baseFeeValue: detail.baseFeeValue || 0,
                }));
            } else if (this.costInfo.hasTimeSlot === 1) {
                // 시간대 개념이 있는 모든 경우 (3차원, 2차원, 1차원)
                Object.keys(this.timeSlotsByGroup).forEach((key) => {
                    this.timeSlotsByGroup[key].forEach((slot) => {
                        const detail = {
                            hourStart: slot.hourStart,
                            hourEnd: slot.hourEnd,
                            timeSlotCode: slot.timeSlotCode,
                            unitPrice: slot.unitPrice,
                        };

                        // 계절 정보 추가 (있는 경우만)
                        if (slot.seasonCode) {
                            detail.seasonCode = slot.seasonCode;
                        }

                        // 요일 정보 추가 (있는 경우만)
                        if (slot.dayType) {
                            detail.dayType = slot.dayType;
                        }

                        payload.details.push(detail);
                    });
                });
            } else if (this.dimensionCount > 0) {
                // 시간대 개념이 없는 경우 (기존 방식)
                payload.details = this.simpleDetails
                    .filter((d) => d.unitPrice > 0)
                    .map((detail) => {
                        const item = {
                            unitPrice: detail.unitPrice,
                        };
                        if (this.costInfo.hasSeason === 1) item.seasonCode = detail.seasonCode;
                        if (this.costInfo.hasTimeSlot === 1) item.timeSlotCode = detail.timeSlotCode;
                        if (this.costInfo.hasDayType === 1) item.dayType = detail.dayType;
                        return item;
                    });
            }

            try {
                const result = await backEndApi.energyBillCalc.saveCostDetail(payload);

                if (result.status === 200) {
                    await this.alertNoti("단위요금 정보가 저장되었습니다.");
                    this.$emit("saved");
                    this.closePanel();
                } else {
                    await this.alertWarning(result.data.message || "저장에 실패했습니다.");
                }
            } catch (error) {
                console.error("저장 실패:", error);
                await this.alertWarning("저장에 실패했습니다.");
            }
        },

        // 24시간 커버 검증 (개선된 버전: 상세한 오류 메시지 반환)
        validate24HourCoverage(slots) {
            if (slots.length === 0) {
                return { valid: false, error: "시간대가 없습니다." };
            }

            const sorted = [...slots].sort((a, b) => a.hourStart - b.hourStart);

            // 첫 시간대가 0시부터 시작하는지
            if (sorted[0].hourStart !== 0) {
                return { valid: false, error: "첫 번째 시간대는 0시부터 시작해야 합니다." };
            }

            // 마지막 시간대가 24시로 끝나는지 (hourEnd는 미포함이므로 24)
            if (sorted[sorted.length - 1].hourEnd !== 24) {
                return {
                    valid: false,
                    error: `마지막 시간대의 종료 시간은 24시여야 합니다. (현재: ${sorted[sorted.length - 1].hourEnd}시)`,
                };
            }

            // 중간에 빈 시간이 없는지 확인 (hourEnd는 미포함이므로 다음 hourStart와 같아야 함)
            for (let i = 0; i < sorted.length - 1; i++) {
                if (sorted[i].hourEnd > sorted[i + 1].hourStart) {
                    return {
                        valid: false,
                        error: `${i + 1}번째 시간대와 ${i + 2}번째 시간대가 겹칩니다. (${sorted[i].hourEnd}시와 ${sorted[i + 1].hourStart}시)`,
                    };
                }
                if (sorted[i].hourEnd < sorted[i + 1].hourStart) {
                    return {
                        valid: false,
                        error: `${i + 1}번째 시간대와 ${i + 2}번째 시간대 사이에 빈 시간이 있습니다. (${sorted[i].hourEnd}시 ~ ${sorted[i + 1].hourStart}시)`,
                    };
                }
            }

            return { valid: true };
        },

        // 라벨 헬퍼
        getSeasonLabel(code) {
            const season = this.seasonOptions.find((s) => s.code === code);
            return season ? season.label : code;
        },
        getSeasonLabelForDisplay(code) {
            const season = this.seasonOptions.find((s) => s.code === code);
            if (!season) return code;
            // "봄/가을" → "봄가을" 변환 (슬래시 제거)
            return season.label.replace(/\//g, "");
        },
        getTimeSlotLabel(code) {
            const slot = this.timeSlotOptions.find((s) => s.code === code);
            return slot ? slot.label : code;
        },
        getDayTypeLabel(code) {
            const day = this.dayTypeOptions.find((d) => d.code === code);
            return day ? day.label : code;
        },
        getTypeName(type) {
            const types = {
                Fixed: "고정",
                Contract: "계약기반",
                Usage: "사용량기반",
                Progressive: "누진제",
                PeakLinked: "피크연동제",
            };
            return types[type] || type;
        },
        formatNumber(value) {
            return new Intl.NumberFormat("ko-KR").format(value || 0);
        },

        // Excel 업로드 (추후 구현)
        importFromExcel() {
            this.alertWarning("Excel 업로드 기능은 추후 구현 예정입니다.");
        },

        closePanel() {
            this.selectedSlot = null;
            this.$emit("close");
        },

        // Alert 헬퍼
        async alertWarning(message) {
            return await this.$swal({
                type: "warning",
                title: "알림",
                text: message,
                confirmButtonText: "확인",
            });
        },
        async alertNoti(message) {
            return await this.$swal({
                type: "success",
                title: "성공",
                text: message,
                confirmButtonText: "확인",
                timer: 2000,
            });
        },
        async alertConfirmWarning(title, text) {
            return await this.$swal({
                type: "question",
                title: title,
                text: text,
                showCancelButton: true,
                confirmButtonText: "확인",
                cancelButtonText: "취소",
            });
        },
    },
};
</script>

<style scoped>
/* 요금제 기본 정보 + 조합 방식 통합 (컴팩트) */
.cost-info-compact {
    margin-bottom: 25px;
    padding: 14px 18px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    border: 1px solid var(--ebc-border);
}

.info-items {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.info-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.info-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}

.info-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.info-divider {
    width: 1px;
    height: 20px;
    background: var(--ebc-border-strong);
    margin: 0 4px;
}

.structure-item {
    margin-left: auto;
    padding: 6px 14px;
    border-radius: var(--ebc-radius-xs);
    border-left: 3px solid;
    display: flex;
    align-items: center;
    gap: 10px;
}

.structure-item.progressive {
    background: var(--ebc-primary-tint);
    border-color: var(--ebc-primary);
}

.structure-item.single {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
}

.structure-item.dual {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
}

.structure-item.triple {
    background: var(--ebc-danger-tint);
    border-color: var(--ebc-danger);
}

.structure-item.none {
    background: var(--ebc-track);
    border-color: var(--ebc-text-muted);
}

.structure-badge {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    white-space: nowrap;
}

.structure-desc {
    font-size: 11px;
    color: var(--ebc-text-sub);
}

/* 단위요금 섹션 */
.detail-section {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-sm);
    padding: 20px;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid var(--ebc-primary);
}

.detail-header h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.detail-actions {
    display: flex;
    gap: 10px;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    background: white;
    border-radius: var(--ebc-radius-xs);
    cursor: pointer;
    transition: all 0.2s;
}

.btn-action:hover {
    background: var(--ebc-surface-sunken);
}

.btn-action.btn-primary {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-action.btn-primary:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}

.btn-action.btn-excel {
    background: var(--ebc-positive);
    color: white;
    border-color: var(--ebc-positive);
}

.btn-action.btn-excel:hover {
    background: var(--ebc-positive);
}

.btn-action.btn-secondary {
    background: var(--ebc-text-sub);
    color: white;
    border-color: var(--ebc-text-sub);
}

.btn-action.btn-secondary:hover:not(:disabled) {
    background: var(--ebc-text);
}

.btn-action.btn-secondary:disabled {
    background: var(--ebc-border-strong);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
}

/* 복제 기능 스타일 */
.copy-cost-section {
    display: flex;
    gap: 8px;
    align-items: center;
}

.copy-cost-select {
    padding: 6px 12px;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    background: white;
    cursor: pointer;
    min-width: 200px;
    transition: all 0.2s;
}

.copy-cost-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

/* 누진제 테이블 */
.progressive-table {
    margin-top: 20px;
}

.detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.detail-table thead th {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 6px 12px;
    font-weight: 600;
    text-align: center;
}

.detail-table tbody td {
    border: 1px solid var(--ebc-border);
    padding: 5px 10px;
    text-align: center;
}

.detail-table tbody td.text-center {
    text-align: center;
}

.range-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
}

.range-inputs input {
    flex: 1;
}

.input-sm {
    padding: 6px 10px;
    border: 1px solid var(--ebc-border-strong);
    border-radius: var(--ebc-radius-xs);
    font-size: 13px;
}

.input-sm:focus {
    outline: none;
    border-color: var(--ebc-primary);
}

.btn-split {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-primary);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-primary);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 4px;
}

.btn-split:hover {
    background: var(--ebc-primary-tint);
    border-color: var(--ebc-primary-hover);
}

.btn-split i {
    font-size: 11px;
}

.btn-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-danger);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-danger);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-delete:hover {
    background: var(--ebc-danger-tint);
}

.btn-delete i {
    font-size: 11px;
}

/* 3차원 */
.triple-dimension {
    margin-top: 20px;
}

.season-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.season-tab {
    padding: 10px 20px;
    border: 1px solid var(--ebc-border);
    background: white;
    border-radius: var(--ebc-radius-xs);
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;
}

.season-tab:hover {
    background: var(--ebc-surface-sunken);
}

.season-tab.active {
    background: var(--ebc-primary);
    color: white;
    border-color: var(--ebc-primary);
}

/* 시간 그리드 컨테이너 */
.hour-grid-container {
    display: flex;
    gap: 20px;
}

.day-type-grid {
    flex: 1;
}

.day-type-grid h4 {
    margin-bottom: 10px;
    padding: 8px 12px;
    background: var(--ebc-surface-sunken);
    border-left: 4px solid var(--ebc-primary);
    font-size: 14px;
}

/* 단일 시간 그리드 (2D 계절, 1D) */
.time-grid {
    flex: 1;
}

.time-grid h4 {
    margin-bottom: 10px;
    padding: 8px 12px;
    background: var(--ebc-surface-sunken);
    border-left: 4px solid var(--ebc-primary);
    font-size: 14px;
}

.hour-grid-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    border: 1px solid var(--ebc-border);
}

.hour-grid-table thead th {
    background: var(--ebc-surface-sunken);
    border-bottom: 2px solid var(--ebc-border);
    border-right: 1px solid var(--ebc-track);
    padding: 10px 8px;
    font-weight: 600;
    text-align: center;
    color: var(--ebc-text);
}

.hour-grid-table thead th:last-child {
    border-right: none;
}

.hour-grid-table tbody tr {
    border-bottom: 1px solid var(--ebc-track);
    transition: all 0.2s;
}

.hour-grid-table tbody tr.selected-block {
    background: var(--ebc-warn-tint);
    box-shadow: inset 3px 0 0 var(--ebc-primary);
}

.hour-grid-table tbody tr.selected-block .hour-cell {
    background: var(--ebc-warn-tint);
    color: var(--ebc-primary);
    font-weight: 700;
}

.hour-grid-table tbody tr.selected-block .load-type-cell,
.hour-grid-table tbody tr.selected-block .price-cell {
    background: var(--ebc-warn-tint);
    font-weight: 600;
}

.hour-grid-table tbody tr.block-start {
    border-top: 2px solid var(--ebc-border);
}

.hour-cell {
    text-align: center;
    vertical-align: middle;
    border-right: 1px solid var(--ebc-track);
    background: var(--ebc-surface-sunken);
    font-weight: 600;
    line-height: 1.6;
    font-size: 12px;
    color: var(--ebc-text-sub);
    padding: 8px 4px;
    white-space: nowrap;
}

.load-type-cell {
    text-align: center;
    vertical-align: middle;
    border-right: 1px solid var(--ebc-track);
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    background: white;
    color: var(--ebc-text);
    padding: 8px;
}

.load-type-cell:hover {
    background: var(--ebc-surface-sunken);
}

.load-type-cell.load-low {
    color: var(--ebc-navy-2);
}

.load-type-cell.load-mid {
    color: var(--ebc-primary);
}

.load-type-cell.load-peak {
    color: var(--ebc-danger);
}

.price-cell {
    text-align: right;
    vertical-align: middle;
    border-right: 1px solid var(--ebc-track);
    padding: 8px 12px;
    cursor: pointer;
    font-weight: 600;
    color: var(--ebc-text);
    background: white;
}

.price-cell:hover {
    background: var(--ebc-surface-sunken);
}

.action-cell {
    text-align: center;
    vertical-align: middle;
    padding: 4px;
    background: white;
}

.btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    padding: 6px 8px;
    color: var(--ebc-text-sub);
    transition: all 0.2s;
    border-radius: var(--ebc-radius-xs);
}

.btn-icon:hover {
    color: var(--ebc-primary);
    background: var(--ebc-warn-tint);
}

/* 블록 편집 패널 */
/* 단위요금 설정 모달 */
.price-setting-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.price-setting-modal {
    background: white;
    border-radius: var(--ebc-radius-sm);
    width: 90%;
    max-width: 500px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    animation: modalFadeIn 0.2s ease-out;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.price-setting-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--ebc-border);
}

.price-setting-modal-header h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--ebc-text);
}

.btn-close-modal {
    background: none;
    border: none;
    color: var(--ebc-text-sub);
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s;
}

.btn-close-modal:hover {
    background: var(--ebc-track);
    color: var(--ebc-text-sub);
}

.price-setting-modal-body {
    padding: 20px;
}

.price-setting-modal-body .form-group {
    margin-bottom: 20px;
}

.price-setting-modal-body .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 14px;
    color: var(--ebc-text);
}

.price-setting-modal-body .form-group-inline {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
}

.price-setting-modal-body .form-group-inline .form-field {
    flex: 1;
}

.price-setting-modal-body .form-field label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 14px;
    color: var(--ebc-text);
}

.price-setting-modal-body .form-input,
.price-setting-modal-body .form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 14px;
    transition: border-color 0.2s;
}

.price-setting-modal-body .form-input:focus,
.price-setting-modal-body .form-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.price-setting-modal-body .button-group {
    display: flex;
    gap: 10px;
    margin-top: 24px;
}

.price-setting-modal-body .button-group button {
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-radius: var(--ebc-radius-xs);
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s;
}

.btn-merge {
    background: var(--ebc-positive);
    color: white;
}

.btn-merge:hover {
    background: var(--ebc-positive);
}

/* Excel 스타일 시간대 블록 리스트 (1차원) */
.time-slot-blocks-container {
    margin-top: 20px;
}

.blocks-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.blocks-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--ebc-text);
}

.btn-add-block {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: var(--ebc-primary);
    color: white;
    border: 1px solid var(--ebc-primary);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-add-block:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
}

.btn-add-block::before {
    content: "";
    font-size: 16px;
    font-weight: bold;
}

.time-slot-blocks-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: var(--ebc-radius-sm);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.time-slot-blocks-table thead {
    background: var(--ebc-surface-sunken);
}

.time-slot-blocks-table thead th {
    padding: 6px 12px;
    text-align: center;
    font-weight: 600;
    font-size: 13px;
    color: var(--ebc-text);
    border-bottom: 2px solid var(--ebc-border);
}

.time-slot-blocks-table tbody tr.block-row {
    border-bottom: 1px solid var(--ebc-track);
    transition: background-color 0.2s;
}

.time-slot-blocks-table tbody tr.block-row:hover {
    background: var(--ebc-surface-sunken);
}

/* 부하 타입별 배경색 */
.time-slot-blocks-table tbody tr.block-row.load-low {
    background-color: var(--ebc-primary-tint);
    /* 연한 파란색 */
}

.time-slot-blocks-table tbody tr.block-row.load-mid {
    background-color: var(--ebc-warn-tint);
    /* 연한 노란색 */
}

.time-slot-blocks-table tbody tr.block-row.load-peak {
    background-color: var(--ebc-warn-tint);
    /* 연한 주황색 */
}

.time-slot-blocks-table tbody tr.block-row.load-low:hover,
.time-slot-blocks-table tbody tr.block-row.load-mid:hover,
.time-slot-blocks-table tbody tr.block-row.load-peak:hover {
    opacity: 0.9;
}

.time-slot-blocks-table tbody td {
    padding: 6px 12px;
    vertical-align: middle;
    text-align: center;
}

.time-select,
.load-type-select,
.price-input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--ebc-border-strong);
    border-radius: var(--ebc-radius-xs);
    font-size: 14px;
    transition: border-color 0.2s;
    background: white;
    cursor: pointer;
}

.time-select:focus,
.load-type-select:focus,
.price-input:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.time-slot-help-text {
    margin-bottom: 12px;
    padding: 10px 12px;
    background: var(--ebc-surface-sunken);
    border-left: 3px solid var(--ebc-primary);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    color: var(--ebc-text);
    line-height: 1.6;
}

.load-type-select {
    cursor: pointer;
}

.price-input {
    text-align: right;
}

/* 1차원 계절만: 입력필드 넓이 제한 */
.season-only-price-input {
    width: auto;
    max-width: 120px;
}

/* 2차원, 3차원용 추가 스타일 */
.day-type-blocks-container {
    margin-top: 20px;
}

.day-type-blocks {
    margin-bottom: 30px;
}

.day-type-blocks:last-child {
    margin-bottom: 0;
}

/* 1D, 2D */
.simple-dimension {
    margin-top: 20px;
}

/* 구조 없음 */
.no-structure {
    text-align: center;
    padding: 40px;
    color: var(--ebc-text-sub);
}

/* Footer */

.btn-cancel,
.btn-save {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-cancel {
    background: var(--ebc-text-sub);
    color: white;
}

.btn-cancel:hover {
    background: var(--ebc-text-sub);
}

.btn-save {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-save:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}
</style>
